<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 relative">
    <BreadCrumbs :projectId="projectId" :sessionId="sessionId" />
    <PhaseNavigation />

    <!-- Main Content -->
    <main class="flex-1 w-full pb-32">
      <section class="w-full max-w-7xl mx-auto px-6 py-8"> <!-- Reduced py-12 to py-8 since nav bar takes some space -->
        <!-- Remove the redundant page header since context is now in the nav bar -->
        
        <!-- Session Management Interface -->
        <template v-if="(activeSessions.length > 0 || deletedSessionsComputed.length > 0) && !routeSessionId">
          <div class="mb-10">
            <!-- Session Status Toggles -->
            <div class="mb-6 flex flex-col gap-2">
              <div v-if="deletedSessionsComputed.length > 0" class="flex items-center gap-2">
                <label class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input type="checkbox" v-model="showDeleted" class="form-checkbox rounded text-red-600 focus:ring-red-500" />
                  Show deleted sessions ({{ deletedSessionsComputed.length }}) - <span class="text-red-600 text-xs">Restorable for 30 days</span>
                </label>
              </div>
            </div>

            <!-- Sessions Grid -->
            <div class="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              <div v-for="session in allSessionsDisplay" :key="session._id"
                data-testid="session-card"
                :class="[
                  'border rounded-xl p-6 transition-all duration-200 relative overflow-hidden',
                  isSessionDeleted(session)
                    ? 'bg-red-50/50 dark:bg-red-950/30 border-red-200 dark:border-red-800 border-2 border-dashed opacity-80 hover:opacity-90'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md border'
                ]">
                
                <!-- Deleted Session Overlay -->
                <div v-if="isSessionDeleted(session)" class="absolute inset-0 bg-gradient-to-br from-red-50/80 to-red-100/40 dark:from-red-950/40 dark:to-red-900/10 pointer-events-none"></div>
                
                <!-- Session Header -->
                <div class="relative z-10 flex items-start justify-between mb-4">
                  <div class="flex items-start gap-3 flex-1 min-w-0">
                    <!-- Enhanced Status Icon with Phase indicator -->
                    <div :class="[
                      'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mt-1',
                      isSessionDeleted(session)
                        ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'
                        : getPhaseColor(session.phase).bg + ' ' + getPhaseColor(session.phase).text
                    ]">
                      <component :is="getPhaseIcon(session.phase, isSessionDeleted(session))" class="w-5 h-5" />
                    </div>
                    
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <!-- Enhanced Status Badge with Phase -->
                        <span v-if="isSessionDeleted(session)" 
                          class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700"
                          data-testid="phase-badge">
                          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clip-rule="evenodd" />
                            <path fill-rule="evenodd" d="M4 5a1 1 0 011-1h10a1 1 0 011 1v1a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM3 10a1 1 0 011-1h12a1 1 0 011 1v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6z" clip-rule="evenodd" />
                          </svg>
                          Deleted
                        </span>
                        <span v-else :class="getPhaseColor(session.phase).badge" data-testid="phase-badge">
                          {{ getPhaseLabel(session.phase) }}
                        </span>
                      </div>
                      
                      <h3 :class="[
                        'text-lg font-semibold mb-1 truncate',
                        isSessionDeleted(session)
                          ? 'text-red-700 dark:text-red-400 line-through decoration-2' 
                          : 'text-gray-900 dark:text-white'
                      ]" :title="session.searchQuery">
                        {{ session.searchQuery }}
                      </h3>
                      
                      <!-- Enhanced metadata with entity info -->
                      <div class="space-y-1">
                        <p class="text-sm text-gray-600 dark:text-gray-400">
                          {{ session.entityType }} • {{ formatDate(getSessionDisplayDate(session)) }}
                        </p>
                        
                        <!-- Entity Information (if available) -->
                        <div v-if="session.entityInfo && session.entityInfo.primary" 
                          class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                          data-testid="entity-info">
                          <span class="font-medium">{{ session.entityInfo.primary.name }}</span>
                          <span class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                            {{ Math.round(session.entityInfo.primary.confidence * 100) }}%
                          </span>
                          <template v-if="session.entityInfo.count > 1">
                            <span class="text-amber-600 dark:text-amber-400">
                              +{{ session.entityInfo.count - 1 }} more
                            </span>
                          </template>
                        </div>
                        
                        <!-- Data Statistics (if available) -->
                        <div v-if="session.dataCount && session.dataCount.total > 0" 
                          class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                          data-testid="data-stats">
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                          </svg>
                          <span>{{ session.dataCount.approved }}/{{ session.dataCount.total }} data points</span>
                          <span v-if="session.dataCount.total > 0" class="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                            {{ Math.round((session.dataCount.approved / session.dataCount.total) * 100) }}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Progress Bar (for active sessions) -->
                <div v-if="!isSessionDeleted(session) && session.progress" 
                  class="relative z-10 mb-4"
                  data-testid="progress-bar">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {{ session.progress.phaseLabel }}
                    </span>
                    <span class="text-xs text-gray-500 dark:text-gray-400">
                      {{ session.progress.progressPercent }}%
                    </span>
                  </div>
                  <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div 
                      :class="getPhaseColor(session.phase).progress"
                      class="h-2 rounded-full transition-all duration-500 ease-out"
                      :style="{ width: `${session.progress.progressPercent}%` }"
                    ></div>
                  </div>
                  <!-- Mini phase indicators -->
                  <div class="flex items-center justify-between mt-1">
                    <div class="flex gap-1">
                      <div v-for="(phase, index) in ['disambiguation', 'data_collection', 'analysis', 'reporting', 'completed']" 
                        :key="phase"
                        :class="[
                          'w-2 h-2 rounded-full',
                          phase === session.phase ? getPhaseColor(phase).progress : 'bg-gray-300 dark:bg-gray-600'
                        ]"
                        :title="getPhaseLabel(phase)"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Enhanced restoration countdown for deleted sessions -->
                <div v-if="isSessionDeleted(session)" class="relative z-10 mb-4">
                  <div v-if="session.canRestore !== false && (session.daysUntilPermanent > 0 || session.status === 'restorable')" :class="[
                    'flex items-center gap-2 p-3 rounded-lg border',
                    (session.daysUntilPermanent <= 3 || session.urgency === 'urgent')
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                      : (session.daysUntilPermanent <= 7 || session.urgency === 'warning') 
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                  ]">
                    <svg :class="[
                      'w-4 h-4 flex-shrink-0',
                      (session.daysUntilPermanent <= 3 || session.urgency === 'urgent')
                        ? 'text-red-600 dark:text-red-400'
                        : (session.daysUntilPermanent <= 7 || session.urgency === 'warning')
                        ? 'text-amber-600 dark:text-amber-400' 
                        : 'text-blue-600 dark:text-blue-400'
                    ]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div>
                      <p :class="[
                        'text-sm font-medium mb-0',
                        (session.daysUntilPermanent <= 3 || session.urgency === 'urgent')
                          ? 'text-red-800 dark:text-red-200'
                          : (session.daysUntilPermanent <= 7 || session.urgency === 'warning')
                          ? 'text-amber-800 dark:text-amber-200'
                          : 'text-blue-800 dark:text-blue-200'
                      ]">
                        {{ session.daysUntilPermanent || 30 }} days left to restore
                      </p>
                      <p :class="[
                        'text-xs mb-0',
                        (session.daysUntilPermanent <= 3 || session.urgency === 'urgent')
                          ? 'text-red-600 dark:text-red-400'
                          : (session.daysUntilPermanent <= 7 || session.urgency === 'warning')
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-blue-600 dark:text-blue-400'
                      ]">
                        {{ (session.daysUntilPermanent <= 3 || session.urgency === 'urgent') 
                           ? '⚠️ Urgent: Session will be permanently deleted soon!'
                           : 'Session will be permanently deleted after 30 days' }}
                      </p>
                    </div>
                  </div>
                  <div v-else class="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <svg class="w-4 h-4 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M9 2a1 1 0 000 2h2a1 0 100-2H9z" clip-rule="evenodd" />
                      <path fill-rule="evenodd" d="M4 5a1 1 0 011-1h10a1 1 0 011 1v1a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM3 10a1 1 0 011-1h12a1 1 0 011 1v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6z" clip-rule="evenodd" />
                    </svg>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-0">
                      Permanently deleted
                    </p>
                  </div>
                </div>
                
                <!-- Enhanced Actions with Primary Action Support -->
                <div class="relative z-10 flex flex-col gap-3">
                  <!-- Primary Action (from API or Smart Generated) -->
                  <template v-if="!isSessionDeleted(session)">
                    <!-- Data Investment Warning (for sessions with collected data) -->
                    <div v-if="isDestructiveDisambiguationChange(session)" 
                      class="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                      <div class="flex items-start gap-2">
                        <svg class="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                        <div>
                          <p class="text-xs font-medium text-amber-800 dark:text-amber-200">Data Collected</p>
                          <p class="text-xs text-amber-700 dark:text-amber-300 mt-1">
                            {{ session.dataCount.total }} data points • Changing entity will reset progress
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      @click="session.primaryAction ? handlePrimaryAction(session.primaryAction, session._id) : openSessionSmart(session._id)"
                      data-testid="primary-action"
                      :class="[
                        'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 w-full justify-center',
                        // Color based on API type or smart determination
                        (session.primaryAction?.type === 'primary' || (!session.primaryAction && session.dataCount?.total > 0))
                          ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                          : session.primaryAction?.type === 'success'
                          ? 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500'
                          : session.primaryAction?.type === 'warning'
                          ? 'bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500'
                          : session.phase === 'completed'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-500'
                          : 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500',
                        'focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-sm'
                      ]"
                    >
                      <component 
                        :is="getActionIconComponent(session)" 
                        class="w-5 h-5 mr-2" 
                      />
                      {{ session.primaryAction?.label || getSmartActionLabel(session) }}
                    </button>
                  </template>
                  
                  <!-- Secondary Actions -->
                  <div class="flex gap-2 justify-between items-center">
                    <!-- Advanced Actions Dropdown (for sessions with data) -->
                    <div v-if="!isSessionDeleted(session) && (session.dataCount?.total > 0 || session.entityInfo?.primary)" 
                      class="relative"
                      data-advanced-actions>
                      <button 
                        @click="toggleAdvancedActions(session._id)"
                        class="inline-flex items-center px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        title="More actions">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
                        </svg>
                      </button>
                      
                      <!-- Advanced Actions Menu -->
                      <div v-if="expandedAdvancedActions === session._id" 
                        class="absolute bottom-full left-0 mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                        <div class="py-1">
                          <!-- Smart Entity Navigation Button -->
                          <button @click="handleSmartEntityView(session); expandedAdvancedActions = null"
                            :class="getEntityButtonStyle(session)"
                            class="flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-colors">
                            <div class="flex items-center gap-2">
                              <component :is="getEntityButtonIcon(session)" class="w-4 h-4" />
                              <span>{{ getEntityButtonText(session) }}</span>
                            </div>
                            <div class="text-xs opacity-75 font-medium">
                              {{ getEntityButtonDestination(session) }}
                            </div>
                          </button>
                          
                          <!-- Session Overview -->
                          <button @click="openSession(session._id); expandedAdvancedActions = null"
                            class="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                            </svg>
                            Session Overview
                          </button>
                          
                          <!-- Conditional Phase Navigation -->
                          <template v-if="session.dataCount?.total > 0">
                            <button @click="navigateToDataOverview(session._id); expandedAdvancedActions = null"
                              class="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                              </svg>
                              Data Overview
                            </button>
                          </template>
                          
                          <template v-if="session.entityInfo?.primary && !session.dataCount?.total">
                            <button @click="navigateToDataCollection(session._id); expandedAdvancedActions = null"
                              class="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                              </svg>
                              Start Data Collection
                            </button>
                          </template>
                          
                          <!-- Destructive Actions Section -->
                          <div class="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
                            <!-- View Disambiguation Results (potentially risky if data exists) -->
                            <template v-if="session.dataCount?.total > 0">
                              <button @click="confirmViewDisambiguation(session); expandedAdvancedActions = null"
                                class="flex items-center w-full px-3 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                </svg>
                                View Disambiguation Results (⚠️ Risk of confusion)
                              </button>
                            </template>
                            
                            <button @click="confirmChangeEntity(session); expandedAdvancedActions = null"
                              :class="[
                                'flex items-center w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700',
                                isDestructiveDisambiguationChange(session)
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-gray-700 dark:text-gray-300'
                              ]">
                              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                              </svg>
                              {{ isDestructiveDisambiguationChange(session) ? 'Change Entity (⚠️ Will Reset Data)' : 'Change Entity' }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Delete Button -->
                    <template v-if="!isSessionDeleted(session)">
                      <button @click="confirmDeleteSession(session._id)"
                        class="inline-flex items-center px-2 py-1 text-red-600 dark:text-red-400 text-xs hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors duration-200"
                        title="Delete Session">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                          </path>
                        </svg>
                      </button>
                    </template>

                    <!-- Deleted Session Actions -->
                    <template v-else-if="isSessionDeleted(session)">
                      <button @click="restoreSession(session._id)"
                        class="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        Restore
                      </button>
                      <button @click="confirmPermanentDelete(session._id, session.searchQuery)"
                        class="inline-flex items-center px-3 py-2 text-red-600 dark:text-red-400 text-sm font-medium border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                        title="Delete Forever">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Create New Session Button (when there are existing sessions) -->
        <template v-if="!routeSessionId && activeSessions.length > 0 && !showNewSessionForm">
          <div class="mb-6 text-center">
            <button 
              @click="showNewSessionForm = true"
              class="inline-flex items-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2">
              <PlusIcon class="w-5 h-5 mr-2" />
              Create New Session
            </button>
          </div>
        </template>

        <!-- Search Form (show when no sessionId and either no sessions exist OR user clicked "Create New Session") -->
        <template v-if="shouldShowSearchForm">
          <div class="bg-white dark:bg-gray-900 border border-blue-100 dark:border-blue-800 rounded-xl shadow-sm p-6 mb-8">
            <form @submit.prevent="onSearch" class="space-y-6" :aria-busy="isSearching">
              <!-- Type Toggle -->
              <div class="flex w-full">
                <div class="flex flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                  <button
                    type="button"
                    :class="[
                      'flex-1 px-4 py-2 rounded-md text-sm font-medium transition',
                      searchType === 'person'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    ]"
                    @click="searchType = 'person'"
                  >Person</button>
                  <button
                    type="button"
                    :class="[
                      'flex-1 px-4 py-2 rounded-md text-sm font-medium transition',
                      searchType === 'company'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    ]"
                    @click="searchType = 'company'"
                  >Company</button>
                </div>
              </div>
              
              <!-- Search Input -->
              <div class="flex flex-col sm:flex-row gap-3">
                <input
                  v-model="search"
                  type="text"
                  :placeholder="`Search for a ${searchType}...`"
                  class="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div class="flex gap-2">
                  <!-- Cancel Button (only show when there are existing sessions) -->
                  <button
                    v-if="activeSessions.length > 0"
                    type="button"
                    @click="showNewSessionForm = false"
                    class="w-full sm:w-auto px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <!-- Search Button -->
                  <button
                    type="submit"
                    :disabled="!search.trim() || isSearching"
                    class="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <span v-if="isSearching">
                      <svg class="animate-spin w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                      </svg>
                      Searching...
                    </span>
                    <span v-else>Search</span>
                  </button>
                </div>
              </div>
              
              <!-- ENHANCED: Updated entity info fields with structured identifiers - Always visible -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-4">
                <template v-if="searchType === 'company'">
                  <!-- Search Filters (Geo & Industry) -->
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">
                      🎯 Search Filters (Improves Results)
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Headquarters</label>
                        <input v-model="companyInfo.headquarters" type="text" placeholder="e.g., Cupertino, CA or San Francisco, CA"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">99.5% result reduction for city-level searches</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Industry</label>
                        <input v-model="companyInfo.industry" type="text" placeholder="e.g., Technology, Finance"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Filters to relevant companies</p>
                      </div>
                    </div>
                  </div>

                  <!-- Verification Data (Improves Confidence) -->
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">
                      ✅ Additional Info (Improves Accuracy)
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
                        <input v-model="companyInfo.website" type="url" placeholder="https://company.com"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Full website URL with protocol</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain</label>
                        <input v-model="companyInfo.domain" type="text" placeholder="company.com (without protocol)"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Primary domain for matching</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stock Ticker</label>
                        <input v-model="companyInfo.ticker" type="text" placeholder="e.g., AAPL"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Stock ticker symbol</p>
                      </div>
                    </div>
                  </div>



                </template>
                <template v-else>
                  <!-- Search Filters (Actually Used by API) -->
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">
                      🎯 Search Filters (Improves Results)
                    </h4>
                    <div class="grid grid-cols-1 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                        <input v-model="personInfo.location" type="text" placeholder="City, State/Country"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Helps narrow search results</p>
                      </div>
                    </div>
                  </div>

                  <!-- Verification Data (Improves Confidence) -->
                  <div class="md:col-span-2">
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">
                      ✅ Additional Info (Improves Accuracy)
                    </h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                        <input v-model="personInfo.company" type="text" placeholder="Current employer"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Current or recent employer</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Title</label>
                        <input v-model="personInfo.jobTitle" type="text" placeholder="Current position"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Current or recent job title</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                        <input v-model="personInfo.email" type="email" placeholder="john@company.com"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Contact email address</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                        <input v-model="personInfo.phone" type="tel" placeholder="+1-555-0123"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Phone number with country code</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">LinkedIn</label>
                        <input v-model="personInfo.linkedIn" type="url" placeholder="https://linkedin.com/in/..."
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">LinkedIn profile URL</p>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Twitter</label>
                        <input v-model="personInfo.twitter" type="text" placeholder="@username or URL"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Twitter handle or URL</p>
                      </div>
                    </div>
                  </div>


                </template>

                <!-- Custom Fields Section -->
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Custom Fields</label>
                  <div class="space-y-2">
                    <div v-for="(field, idx) in (searchType === 'company' ? companyInfo.customFields : personInfo.customFields)" :key="'custom-field-' + idx" class="flex gap-2">
                      <input v-model="field.label" type="text" placeholder="Field name"
                        class="w-1/3 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <input v-model="field.value" type="text" placeholder="Value"
                        class="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <button v-if="idx > 0" type="button" @click="(searchType === 'company' ? companyInfo.customFields : personInfo.customFields).splice(idx, 1)"
                        class="text-xs text-red-500 hover:underline">Remove</button>
                    </div>
                    <button type="button" @click="(searchType === 'company' ? companyInfo.customFields : personInfo.customFields).push({ label: '', value: '' })"
                      class="text-xs text-blue-600 hover:underline">+ Add Custom Field</button>
                  </div>
                </div>

                <!-- File upload section -->

                
              </div>
            </form>

            <!-- FIXED: Search Loading State - Shows immediately below form -->
            <div v-if="hasSearched && isSearching" class="mt-8 space-y-6">
              <div class="flex items-center justify-center py-8">
                <div class="flex items-center space-x-3">
                  <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span class="text-gray-600 dark:text-gray-400">Searching for matches...</span>
                </div>
              </div>
              
              <!-- Skeleton Loading Cards -->
              <div class="space-y-4">
                <div v-for="i in 3" :key="i" class="animate-pulse">
                  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div class="flex items-start justify-between">
                      <div class="flex-1 space-y-3">
                        <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                        <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        <div class="space-y-2">
                          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                        </div>
                      </div>
                      <div class="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- IMPROVED: Show "Add New" option when no results found -->
            <div v-else-if="hasSearched && !isSearching && (!candidates || candidates.length === 0)" class="mt-8">
              <div class="bg-white dark:bg-gray-900 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
                <div class="text-gray-500 dark:text-gray-400 mb-6">
                  <svg class="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <p class="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No matches found for "{{ search }}"</p>
                  <p class="text-sm mb-6">The {{ searchType }} you're looking for might not be in our databases yet</p>
                </div>
                
                <button 
                  @click="showAddNewEntityForm = true"
                  class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors font-medium"
                >
                  <PlusIcon class="w-5 h-5" />
                  Add New {{ searchType === 'company' ? 'Company' : 'Person' }}
                </button>
                
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  You can add the entity manually and we'll research it for you
                </p>
              </div>
            </div>
          </div>
        </template>

        <!-- FIXED: Candidates Section - Show after search completes -->
        <div v-if="!isSearching && candidates && candidates.length" class="space-y-4 mt-8">
          <!-- Search Results Header with Search Again Button -->
          <div v-if="hasSearched" class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-4">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Search Results
              </h3>
              <span class="text-sm text-gray-500 dark:text-gray-400">
                {{ candidates.length }} {{ candidates.length === 1 ? 'match' : 'matches' }} found
              </span>
            </div>
            <button
              @click="handleSearchAgain"
              :disabled="hasCollectionStarted"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
              :title="hasCollectionStarted ? 'Data collection has already been started for this session' : ''"
            >
              <MagnifyingGlassIcon class="w-4 h-4" />
              Search Again
            </button>
          </div>
          
          <!-- Standard Data Option (Always First) -->
          <div 
            @click="showAddNewEntityForm = true"
            class="cursor-pointer transition-all duration-200 hover:shadow-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 rounded-xl px-6 py-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <div class="flex items-center gap-4">
              <!-- Document Icon -->
              <div class="w-12 h-12 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                <svg class="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              
              <!-- Content -->
              <div class="flex-1">
                <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Use Standard Data Only
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Proceed with "{{ search }}" using your provided details. Add enriched data later.
                </p>
                <span class="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 font-medium mt-2">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Baseline information only
                </span>
              </div>
            </div>
          </div>
          
            <!-- Top Candidate -->
            <div
              v-if="topCandidate"
              class="relative transition-all duration-200 hover:shadow-lg border-2 rounded-xl px-6 py-5"
              :class="{
                'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 shadow-md': isCandidateSelected(topCandidate),
                'bg-white dark:bg-gray-900 border-blue-200 dark:border-blue-700 shadow-sm': !isCandidateSelected(topCandidate)
              }"
            >
              <!-- Top Candidate Badge -->
              <div class="absolute top-3 right-3">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                  {{ topCandidate.source }} • Score: {{ Math.round(topCandidate.totalScore * 100) }}%
                </span>
              </div>
              
              <div class="flex items-start gap-4">
                <!-- Avatar -->
                 <div class="w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img v-if="topCandidate.image" :src="topCandidate.image" 
                    class="w-full h-full object-contain" 
                    :alt="topCandidate.name" />
                  <span v-else class="text-xl font-bold text-blue-600 dark:text-blue-300">
                    {{ topCandidate.name[0] }}
                  </span>
                </div>
                
                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex-1 min-w-0 pr-4">
                      <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">{{ topCandidate.name }}</h3>
                      
                      <!-- Enhanced context display -->
                      <template v-if="topCandidate.location || topCandidate.context?.linkedin || topCandidate.context?.allUrls?.linkedin">
                        <div class="mb-2">
                          <div class="flex flex-wrap gap-2 text-sm">
                            <template v-if="topCandidate.location">
                              <span class="text-gray-600 dark:text-gray-400">
                                📍 {{ topCandidate.location }}
                              </span>
                            </template>
                            <template v-if="topCandidate.context?.linkedin || topCandidate.context?.allUrls?.linkedin">
                              <a :href="(topCandidate.context?.linkedin || topCandidate.context?.allUrls?.linkedin).startsWith('http') ? (topCandidate.context?.linkedin || topCandidate.context?.allUrls?.linkedin) : `https://${topCandidate.context?.linkedin || topCandidate.context?.allUrls?.linkedin}`" 
                                target="_blank"
                                class="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                                💼 LinkedIn
                              </a>
                            </template>
                          </div>
                        </div>
                      </template>
                      
                      <p class="text-gray-600 dark:text-gray-400 text-sm mb-3">{{ topCandidate.description }}</p>
                      
                      <!-- Data quality indicators -->
                      <div class="flex flex-wrap gap-2 mb-3">
                        <template v-if="topCandidate.aviatoId">
                          <span class="px-2 py-1 bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-xs rounded-md border border-purple-200 dark:border-purple-800">
                            🆔 Entity ID: {{ topCandidate.aviatoId.slice(-8) }}
                          </span>
                        </template>
                        
                        <template v-if="topCandidate.combinedSources?.primarySources?.length">
                          <span class="px-2 py-1 bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs rounded-md border border-green-200 dark:border-green-800">
                            ✅ {{ topCandidate.combinedSources.primarySources.length }} Verified Sources
                          </span>
                        </template>
                        
                        <template v-if="topCandidate.metadata?.identificationFields">
                          <span class="px-2 py-1 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs rounded-md border border-blue-200 dark:border-blue-800">
                            📊 Score: {{ Math.round((topCandidate.confidence || topCandidate.totalScore || 0) * 100) }}%
                          </span>
                        </template>
                        <template v-if="topCandidate.metadata?.hasEnrichmentData">
                          <span class="px-2 py-1 bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs rounded-md border border-green-200 dark:border-green-800">
                            ✅ Enriched Data
                          </span>
                        </template>
                      </div>
                    </div>
                    

                  </div>
                  
                  <!-- Expandable Details -->
                  <button 
                    @click.stop="expandMoreInfo('top')" 
                    class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    {{ expandedMoreInfo === 'top' ? 'Hide Details' : 'Show Details' }}
                  </button>
                  
                  <transition name="fade">
                    <div v-if="expandedMoreInfo === 'top'" class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      
                      <!-- Enhanced Context Information -->
                      <template v-if="topCandidate.location || topCandidate.context || topCandidate.metadata">
                        <div class="mb-4">
                          <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Entity Information</h4>
                          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                            <template v-if="topCandidate.location">
                              <div class="flex items-center gap-2">
                                <span class="font-medium text-gray-600 dark:text-gray-400">Location:</span>
                                <span class="text-gray-700 dark:text-gray-300 truncate">{{ topCandidate.location }}</span>
                              </div>
                            </template>
                            <template v-if="topCandidate.context?.linkedin">
                              <div class="flex items-center gap-2">
                                <span class="font-medium text-gray-600 dark:text-gray-400">LinkedIn:</span>
                                <a :href="topCandidate.context.linkedin.startsWith('http') ? topCandidate.context.linkedin : `https://${topCandidate.context.linkedin}`" 
                                  target="_blank" 
                                  class="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 truncate">
                                  {{ topCandidate.context.linkedin }}
                                </a>
                              </div>
                            </template>
                            <template v-if="topCandidate.confidence">
                              <div class="flex items-center gap-2">
                                <span class="font-medium text-gray-600 dark:text-gray-400">Confidence:</span>
                                <span class="text-gray-700 dark:text-gray-300">{{ Math.round(topCandidate.confidence * 100) }}%</span>
                              </div>
                            </template>
                            <template v-if="topCandidate.aviatoId">
                              <div class="flex items-center gap-2">
                                <span class="font-medium text-gray-600 dark:text-gray-400">Aviato ID:</span>
                                <span class="text-gray-700 dark:text-gray-300 font-mono text-xs truncate">{{ topCandidate.aviatoId }}</span>
                              </div>
                            </template>

                          </div>
                        </div>
                      </template>
                      
                      <!-- Alternative Sources Information -->
                      <template v-if="topCandidate.alternativeSources?.length">
                        <div class="mb-4">
                          <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Alternative Sources ({{ topCandidate.alternativeSources.length }})
                          </h4>
                          <div class="space-y-2">
                            <div v-for="alt in topCandidate.alternativeSources.slice(0, 3)" :key="`${alt.source}-${alt.name}`"
                              class="p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                              <div class="flex items-center justify-between">
                                <div>
                                  <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ alt.name }}</span>
                                  <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                    {{ alt.source }} • {{ Math.round(alt.totalScore * 100) }}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      
                      <button 
                        @click="expandSources('top')" 
                        class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                      >
                        {{ expandedSources === 'top' ? 'Hide Sources' : 'Show Sources' }}
                      </button>
                      
                      <!-- Enhanced Sources Display -->
                      <div v-if="expandedSources === 'top'" class="mt-3 space-y-3">
                        <!-- Primary Sources -->
                        <template v-if="topCandidate.combinedSources?.primarySources?.length">
                          <div class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                            <h5 class="text-sm font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center gap-1">
                              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                              </svg>
                              Primary Sources ({{ topCandidate.combinedSources.primarySources.length }})
                            </h5>
                            <div class="space-y-2">
                              <div v-for="source in topCandidate.combinedSources.primarySources" :key="source.url" 
                                class="p-2 bg-white dark:bg-gray-800 rounded border border-green-200 dark:border-green-700">
                                <div class="flex items-start justify-between">
                                  <div class="flex-1">
                                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ source.title }}</p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ source.type.replace('_', ' ') }}</p>
                                    <div v-if="source.relevantFor?.length" class="mt-1">
                                      <span v-for="item in source.relevantFor" :key="item"
                                        class="inline-block text-xs bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded mr-1 mb-1">
                                        {{ item.replace('_', ' ') }}
                                      </span>
                                    </div>
                                  </div>
                                  <a v-if="source.url" :href="source.url" target="_blank" 
                                    class="text-green-600 dark:text-green-400 hover:underline text-xs ml-2 flex-shrink-0">
                                    View →
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <!-- Secondary Sources -->
                        <template v-if="topCandidate.combinedSources?.secondarySources?.length">
                          <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <h5 class="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-1">
                              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                              </svg>
                              Secondary Sources ({{ topCandidate.combinedSources.secondarySources.length }})
                            </h5>
                            <div class="space-y-2">
                              <div v-for="source in topCandidate.combinedSources.secondarySources" :key="source.url" 
                                class="p-2 bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700">
                                <div class="flex items-start justify-between">
                                  <div class="flex-1">
                                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ source.title }}</p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ source.type.replace('_', ' ') }}</p>
                                    <div v-if="source.relevantFor?.length" class="mt-1">
                                      <span v-for="item in source.relevantFor" :key="item"
                                        class="inline-block text-xs bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded mr-1 mb-1">
                                        {{ item.replace('_', ' ') }}
                                      </span>
                                    </div>
                                  </div>
                                  <a v-if="source.url" :href="source.url" target="_blank" 
                                    class="text-blue-600 dark:text-blue-400 hover:underline text-xs ml-2 flex-shrink-0">
                                    View →
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <!-- Verification Info -->
                        <template v-if="topCandidate.combinedSources?.lastVerified">
                          <div class="p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                            <div class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                              </svg>
                              <span>Last verified: {{ topCandidate.combinedSources.lastVerified }}</span>
                            </div>
                            <p v-if="topCandidate.combinedSources.verificationNotes" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {{ topCandidate.combinedSources.verificationNotes }}
                            </p>
                          </div>
                        </template>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
              
              <!-- Select & Continue Button (Primary styling for top candidate) -->
              <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  @click="async () => { await selectCandidate(topCandidate); await goToDataCollection(); }"
                  :disabled="!projectId || !sessionId"
                  :class="[
                    'w-full px-4 py-2.5 rounded-lg font-medium shadow-sm transition-all duration-200 flex items-center justify-center gap-2',
                    projectId && sessionId
                      ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  ]"
                >
                  Select & Continue
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
            <!-- UPDATED: Other Candidates with enhanced data display and full expandable details -->
          <div v-for="candidate in otherCandidates" :key="`${candidate.source}-${candidate.name}`" 
            class="relative transition-all duration-200 hover:shadow-lg border-2 rounded-xl px-6 py-4"
            :class="{
              'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 shadow-md': isCandidateSelected(candidate),
              'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-sm': !isCandidateSelected(candidate)
            }">
            
            <!-- Candidate source and score -->
            <div class="absolute top-3 right-3">
              <span class="text-xs text-gray-500 dark:text-gray-400">
                {{ candidate.source }} • {{ Math.round(candidate.totalScore * 100) }}%
              </span>
            </div>
            
            <div class="flex items-start gap-4">
              <!-- Avatar -->
              <div class="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img v-if="candidate.image" :src="candidate.image" 
                  class="w-full h-full object-contain" 
                  :alt="candidate.name" />
                <span v-else class="text-lg font-bold text-gray-600 dark:text-gray-400">
                  {{ candidate.name[0] }}
                </span>
              </div>
              
              <!-- Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex-1 min-w-0 pr-4">
                    <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">{{ candidate.name }}</h3>
                    
                    <!-- Enhanced context display for other candidates -->
                    <template v-if="candidate.location || candidate.context?.linkedin">
                      <div class="mb-2">
                        <div class="flex flex-wrap gap-2 text-sm">
                          <template v-if="candidate.location">
                            <span class="text-gray-600 dark:text-gray-400">
                              📍 {{ candidate.location }}
                            </span>
                          </template>
                          <template v-if="candidate.context?.linkedin">
                            <a :href="candidate.context.linkedin.startsWith('http') ? candidate.context.linkedin : `https://${candidate.context.linkedin}`" 
                              target="_blank"
                              class="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                              💼 LinkedIn
                            </a>
                          </template>
                        </div>
                      </div>
                    </template>
                    
                    <p class="text-gray-600 dark:text-gray-400 text-sm mb-2">{{ candidate.description }}</p>
                    
                    <!-- Enhanced indicators for other candidates -->
                    <div class="flex flex-wrap gap-2 mb-2">
                      <template v-if="candidate.confidence || candidate.totalScore">
                        <span class="px-2 py-1 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs rounded-md border border-blue-200 dark:border-blue-800">
                          📊 Score: {{ Math.round((candidate.confidence || candidate.totalScore || 0) * 100) }}%
                        </span>
                      </template>
                      
                      <template v-if="candidate.aviatoId">
                        <span class="px-2 py-1 bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-xs rounded-md border border-purple-200 dark:border-purple-800">
                          🆔 {{ candidate.aviatoId.slice(-8) }}
                        </span>
                      </template>
                      
                      <template v-if="candidate.metadata?.hasEnrichmentData">
                        <span class="px-2 py-1 bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs rounded-md border border-green-200 dark:border-green-800">
                          ✅ Enriched Data
                        </span>
                      </template>
                      

                    </div>
                  </div>
                  

                </div>
                
                <!-- Expandable Details Button -->
                <button 
                  @click.stop="expandMoreInfo(`candidate-${candidate.aviatoId || candidate.id || `${candidate.name}-${candidate.source}`}`)" 
                  class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mt-2"
                >
                  {{ expandedMoreInfo === `candidate-${candidate.aviatoId || candidate.id || `${candidate.name}-${candidate.source}`}` ? 'Hide Details' : 'Show Details' }}
                </button>
                
                <!-- Expandable Details Content -->
                <transition name="fade">
                  <div v-if="expandedMoreInfo === `candidate-${candidate.aviatoId || candidate.id || `${candidate.name}-${candidate.source}`}`" class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    
                    <!-- Enhanced Context Information -->
                    <template v-if="candidate.location || candidate.context || candidate.metadata">
                      <div class="mb-4">
                        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Entity Information</h4>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <template v-if="candidate.location">
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-600 dark:text-gray-400">Location:</span>
                              <span class="text-gray-700 dark:text-gray-300 truncate">{{ candidate.location }}</span>
                            </div>
                          </template>
                          <template v-if="candidate.context?.linkedin">
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-600 dark:text-gray-400">LinkedIn:</span>
                              <a :href="candidate.context.linkedin.startsWith('http') ? candidate.context.linkedin : `https://${candidate.context.linkedin}`" 
                                target="_blank" 
                                class="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 truncate">
                                {{ candidate.context.linkedin }}
                              </a>
                            </div>
                          </template>
                          <template v-if="candidate.confidence">
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-600 dark:text-gray-400">Confidence:</span>
                              <span class="text-gray-700 dark:text-gray-300">{{ Math.round(candidate.confidence * 100) }}%</span>
                            </div>
                          </template>
                          <template v-if="candidate.aviatoId">
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-600 dark:text-gray-400">Aviato ID:</span>
                              <span class="text-gray-700 dark:text-gray-300 font-mono text-xs truncate">{{ candidate.aviatoId }}</span>
                            </div>
                          </template>

                          <template v-if="candidate.metadata?.lastUpdated">
                            <div class="flex items-center gap-2">
                              <span class="font-medium text-gray-600 dark:text-gray-400">Last Updated:</span>
                              <span class="text-gray-700 dark:text-gray-300 text-xs">{{ new Date(candidate.metadata.lastUpdated).toLocaleDateString() }}</span>
                            </div>
                          </template>
                        </div>
                      </div>
                    </template>
                    
                    <!-- Structured Identifier Matches -->
                    <template v-if="candidate.structuredIdentifierMatches && candidate.structuredIdentifierMatches.totalChecks > 0">
                      <div class="mb-4">
                        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Identifier Matches ({{ candidate.structuredIdentifierMatches.totalMatches }}/{{ candidate.structuredIdentifierMatches.totalChecks }})
                        </h4>
                        <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div class="flex items-center justify-between">
                            <div>
                              <span class="text-sm font-medium text-blue-900 dark:text-blue-100">Overall Confidence</span>
                              <span class="text-xs text-blue-700 dark:text-blue-300 ml-2">
                                {{ Math.round(candidate.structuredIdentifierMatches.overallConfidence * 100) }}%
                              </span>
                            </div>
                            <div class="text-xs text-blue-700 dark:text-blue-300">
                              {{ candidate.structuredIdentifierMatches.totalMatches }} matches
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    
                    <!-- Metadata Information -->
                    <template v-if="candidate.metadata?.identificationFields">
                      <div class="mb-4">
                        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Available Data Fields
                        </h4>
                        <div class="grid grid-cols-2 gap-2 text-xs">
                          <div v-if="candidate.metadata.identificationFields.hasLocation" class="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <span>✅</span> Location
                          </div>
                          <div v-if="candidate.metadata.identificationFields.hasLinkedIn" class="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <span>✅</span> LinkedIn
                          </div>
                          <div v-if="candidate.metadata.identificationFields.hasTwitter" class="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <span>✅</span> Twitter
                          </div>
                          <div v-if="candidate.metadata.identificationFields.hasWebsite" class="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <span>✅</span> Website
                          </div>
                          <div class="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            <span>🔗</span> {{ candidate.metadata.identificationFields.urlCount || 0 }} URLs
                          </div>
                        </div>
                      </div>
                    </template>
                    
                    <!-- Sources Toggle Button -->
                    <button 
                      @click.stop="expandSources(`candidate-${candidate.aviatoId || candidate.id || `${candidate.name}-${candidate.source}`}`)" 
                      class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      {{ expandedSources === `candidate-${candidate.aviatoId || candidate.id || `${candidate.name}-${candidate.source}`}` ? 'Hide Sources' : 'Show Sources' }}
                    </button>
                    
                    <!-- Enhanced Sources Display -->
                    <div v-if="expandedSources === `candidate-${candidate.aviatoId || candidate.id || `${candidate.name}-${candidate.source}`}`" class="mt-3 space-y-3">
                      <!-- Primary Sources -->
                      <template v-if="candidate.combinedSources?.primarySources?.length">
                        <div class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <h5 class="text-sm font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center gap-1">
                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                            </svg>
                            Primary Sources ({{ candidate.combinedSources.primarySources.length }})
                          </h5>
                          <div class="space-y-2">
                            <div v-for="source in candidate.combinedSources.primarySources" :key="source.url" 
                              class="p-2 bg-white dark:bg-gray-800 rounded border border-green-200 dark:border-green-700">
                              <div class="flex items-start justify-between">
                                <div class="flex-1">
                                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ source.title }}</p>
                                  <p class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ source.type.replace('_', ' ') }}</p>
                                  <div v-if="source.relevantFor?.length" class="mt-1">
                                    <span v-for="item in source.relevantFor" :key="item"
                                      class="inline-block text-xs bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded mr-1 mb-1">
                                      {{ item.replace('_', ' ') }}
                                    </span>
                                  </div>
                                </div>
                                <a v-if="source.url" :href="source.url" target="_blank" 
                                  class="text-green-600 dark:text-green-400 hover:underline text-xs ml-2 flex-shrink-0">
                                  View →
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Secondary Sources -->
                      <template v-if="candidate.combinedSources?.secondarySources?.length">
                        <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <h5 class="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-1">
                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                            </svg>
                            Secondary Sources ({{ candidate.combinedSources.secondarySources.length }})
                          </h5>
                          <div class="space-y-2">
                            <div v-for="source in candidate.combinedSources.secondarySources" :key="source.url" 
                              class="p-2 bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700">
                              <div class="flex items-start justify-between">
                                <div class="flex-1">
                                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ source.title }}</p>
                                  <p class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ source.type.replace('_', ' ') }}</p>
                                  <div v-if="source.relevantFor?.length" class="mt-1">
                                    <span v-for="item in source.relevantFor" :key="item"
                                      class="inline-block text-xs bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded mr-1 mb-1">
                                      {{ item.replace('_', ' ') }}
                                    </span>
                                  </div>
                                </div>
                                <a v-if="source.url" :href="source.url" target="_blank" 
                                  class="text-blue-600 dark:text-blue-400 hover:underline text-xs ml-2 flex-shrink-0">
                                  View →
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- All Sources Audit Trail -->
                      <template v-if="candidate.allSources?.length">
                        <div class="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                          <h5 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1">
                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
                            </svg>
                            Source Audit Trail ({{ candidate.allSources.length }})
                          </h5>
                          <div class="space-y-2">
                            <div v-for="source in candidate.allSources" :key="source.source"
                              class="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                              <div class="flex-1">
                                <div class="flex items-center gap-2">
                                  <span class="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">{{ source.source }}</span>
                                  <template v-if="source.isPrimary">
                                    <span class="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300 text-xs rounded">
                                      Primary
                                    </span>
                                  </template>
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  <span>Confidence: {{ Math.round(source.confidence * 100) }}%</span>
                                  <span class="mx-1">•</span>
                                  <span>{{ source.hasContext ? `${source.contextKeys?.length || 0} context` : 'No context' }}</span>
                                  <span class="mx-1">•</span>
                                  <span>{{ source.hasSources ? `${source.sourceCount || 0} sources` : 'No sources' }}</span>
                                </div>
                              </div>
                              <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {{ Math.round(source.totalScore * 100) }}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Verification Info -->
                      <template v-if="candidate.combinedSources?.lastVerified">
                        <div class="p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                          <div class="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                            </svg>
                            <span>Last verified: {{ candidate.combinedSources.lastVerified }}</span>
                          </div>
                          <p v-if="candidate.combinedSources.verificationNotes" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {{ candidate.combinedSources.verificationNotes }}
                          </p>
                        </div>
                      </template>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
            
            <!-- Select & Continue Button -->
            <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                @click="async () => { await selectCandidate(candidate); await goToDataCollection(); }"
                :disabled="!projectId || !sessionId"
                :class="[
                  'w-full px-4 py-2.5 rounded-lg font-medium shadow-sm transition-all duration-200 flex items-center justify-center gap-2',
                  projectId && sessionId
                    ? 'bg-gray-600 hover:bg-gray-700 text-white cursor-pointer'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                ]"
              >
                Select & Continue
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Skeleton Loader -->
          <div v-if="loading && (!candidates || !candidates.length)" class="space-y-8 mt-8">
            <!-- Top Candidate Skeleton -->
            <div class="border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40 rounded-xl p-8 shadow flex flex-col gap-2 animate-pulse">
              <div class="flex items-center gap-6">
                <div class="w-20 h-20 rounded bg-blue-100 dark:bg-blue-800"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-6 bg-blue-100 dark:bg-blue-800 rounded w-1/2"></div>
                  <div class="h-4 bg-blue-100 dark:bg-blue-800 rounded w-1/3"></div>
                  <div class="flex gap-2 mt-2">
                    <div class="h-4 w-16 bg-blue-100 dark:bg-blue-800 rounded"></div>
                    <div class="h-4 w-16 bg-blue-100 dark:bg-blue-800 rounded"></div>
                  </div>
                </div>
              </div>
              <div class="h-4 bg-blue-100 dark:bg-blue-800 rounded w-1/4 mt-6"></div>
            </div>
            <!-- Other Candidates Skeletons -->
            <div v-for="n in 2" :key="n" class="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl p-8 shadow flex flex-col gap-2 animate-pulse">
              <div class="flex items-center gap-6">
                <div class="w-16 h-16 rounded bg-gray-100 dark:bg-gray-700"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-5 bg-gray-100 dark:bg-gray-700 rounded w-1/2"></div>
                  <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-1/3"></div>
                  <div class="flex gap-2 mt-2">
                    <div class="h-4 w-12 bg-gray-100 dark:bg-gray-700 rounded"></div>
                    <div class="h-4 w-12 bg-gray-100 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              </div>
              <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-1/4 mt-6"></div>
            </div>
          </div>

          <!-- No Results Found Message -->
          <div
            v-if="hasSearched && !loading && (!candidates || !candidates.length)"
            class="text-center text-gray-400 dark:text-gray-500 py-12 mt-8"
          >
            <div class="flex flex-col items-center gap-2">
              <svg class="w-10 h-10 mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75h.008v.008H9.75V9.75zm4.5 0h.008v.008h-.008V9.75zm-6.364 6.364a7.5 7.5 0 1110.607 0M15 15.75a3 3 0 01-6 0"/>
              </svg>
              <span>No results found. Try a different search.</span>
            </div>
          </div>
          </div>
      </section>
    </main>
    
    <!-- Floating Agent Button -->
    <button
      @click="isAgentOpen = !isAgentOpen"
      class="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 flex items-center justify-center group"
      :class="{ 'scale-95': isAgentOpen }"
    >
      <!-- Close icon when open -->
      <svg v-if="isAgentOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
      <!-- AI Chat icon when closed -->
      <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    </button>

    <!-- Agent Chat Overlay Panel -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isAgentOpen"
        ref="agentPanelRef"
        class="fixed bottom-24 right-6 w-[400px] max-h-[600px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 flex flex-col overflow-hidden"
      >
        <AgentChatSidebar
          :activeProject="projectContext"
          :loading="loading"
          @close="isAgentOpen = false"
        />
      </div>
    </Transition>

    <!-- Enhanced Session Deletion Modal -->
    <template v-if="showDeleteModal">
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg mx-auto flex flex-col max-h-[90vh]">
          <!-- Fixed Header -->
          <div class="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">Delete Research Session</h2>
          </div>
          
          <!-- Scrollable Content -->
          <div class="overflow-y-auto flex-1 p-6 space-y-6">
            <!-- Deletion Type Selection -->
            <div class="space-y-4">
              <p class="text-gray-700 dark:text-gray-300">Choose how to handle this research session and its data:</p>
              
              <!-- Soft Delete (Default) -->
              <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                     :class="deleteType === 'soft' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'">
                <input type="radio" v-model="deleteType" value="soft" class="mt-1 text-blue-600" />
                <div>
                  <div class="font-medium text-gray-900 dark:text-white">Soft Delete (Recommended)</div>
                  <div class="text-sm text-gray-600 dark:text-gray-400">Hide session but preserve research data. Can be restored within 30 days.</div>
                </div>
              </label>
              
              <!-- Hard Delete -->
              <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                     :class="deleteType === 'hard' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700'">
                <input type="radio" v-model="deleteType" value="hard" class="mt-1 text-red-600" />
                <div>
                  <div class="font-medium text-red-700 dark:text-red-400">Permanent Delete</div>
                  <div class="text-sm text-red-600 dark:text-red-400">⚠️ Immediately delete session and all data. Cannot be undone!</div>
                </div>
              </label>
            </div>

            <!-- Data Cascade Options -->
            <div class="space-y-3">
              <h3 class="font-medium text-gray-900 dark:text-white">Research Data Handling:</h3>
              
              <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                     :class="!cascadeData ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700'">
                <input type="radio" v-model="cascadeData" :value="false" class="mt-1 text-green-600" />
                <div>
                  <div class="font-medium text-gray-900 dark:text-white">Preserve Research Data (Recommended)</div>
                  <div class="text-sm text-gray-600 dark:text-gray-400">Keep valuable articles, entity records, and AI embeddings. Only remove session reference.</div>
                </div>
              </label>
              
              <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                     :class="cascadeData ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-gray-200 dark:border-gray-700'">
                <input type="radio" v-model="cascadeData" :value="true" class="mt-1 text-orange-600" />
                <div>
                  <div class="font-medium text-gray-900 dark:text-white">Delete All Research Data</div>
                  <div class="text-sm text-gray-600 dark:text-gray-400">Remove session AND all associated articles, entity records, and AI embeddings.</div>
                </div>
              </label>
            </div>

            <!-- Deletion Reason -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reason for deletion:</label>
              <select v-model="deleteReason" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                <option value="cleanup">Session cleanup</option>
                <option value="failed">Failed research</option>
                <option value="privacy">Privacy request</option>
                <option value="migration">Data migration</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <!-- Data Impact Summary -->
            <div v-if="dataStats" class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 class="font-medium text-gray-900 dark:text-white mb-3">Data Impact Summary</h3>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-gray-600 dark:text-gray-400">Articles:</span>
                  <span class="font-medium ml-2">{{ dataStats.articles || 0 }}</span>
                </div>
                <div>
                  <span class="text-gray-600 dark:text-gray-400">Collections:</span>
                  <span class="font-medium ml-2">{{ dataStats.dataCollections || 0 }}</span>
                </div>
                <div>
                  <span class="text-gray-600 dark:text-gray-400">Embeddings:</span>
                  <span class="font-medium ml-2">{{ dataStats.vectorEmbeddings || 0 }}</span>
                </div>
                <div>
                  <span class="text-gray-600 dark:text-gray-400">Storage:</span>
                  <span class="font-medium ml-2">{{ dataStats.totalStorageMB || 0 }}MB</span>
                </div>
              </div>
              <div v-if="dataStats.recommendations && dataStats.recommendations.length > 0" class="mt-3 text-xs text-blue-600 dark:text-blue-400">
                <div v-for="rec in dataStats.recommendations" :key="rec">{{ rec }}</div>
              </div>
            </div>

            <!-- Warning for dangerous combinations -->
            <div v-if="deleteType === 'hard' && cascadeData" class="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
              <div class="flex items-center gap-2 text-red-800 dark:text-red-400 text-sm font-medium">
                <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                This will permanently delete the session and ALL valuable research data including articles, entity records, and expensive AI embeddings.
              </div>
            </div>
          </div>
          
          <!-- Fixed Footer with Action Buttons -->
          <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end bg-gray-50 dark:bg-gray-800/50">
            <button type="button" @click="showDeleteModal = false"
              class="px-4 py-2 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-600 transition border border-gray-300 dark:border-gray-600">
              Cancel
            </button>
            <button type="button" @click="deleteSession"
              :class="[
                'px-6 py-2 rounded-lg font-semibold shadow transition',
                deleteType === 'hard' && cascadeData
                  ? 'bg-gradient-to-r from-red-600 to-red-800 text-white hover:from-red-700 hover:to-red-900'
                  : deleteType === 'hard'
                  ? 'bg-gradient-to-r from-orange-600 to-orange-800 text-white hover:from-orange-700 hover:to-orange-900'
                  : 'bg-gradient-to-r from-blue-600 to-blue-800 text-white hover:from-blue-700 hover:to-blue-900'
              ]">
              {{ deleteType === 'soft' ? 'Soft Delete' : cascadeData ? 'Delete Everything' : 'Delete Session Only' }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Permanent Delete Confirmation Modal -->
    <ConfirmationModal
      :show="showPermanentDeleteModal && sessionToDelete !== null"
      title="Permanently Delete Session"
      description="This action cannot be undone."
      :icon="TrashIcon"
      icon-type="danger"
      confirm-text="Delete Forever"
      cancel-text="Keep Session"
      loading-text="Deleting..."
      :loading="deletingSession"
      @confirm="handlePermanentDelete"
      @cancel="cancelPermanentDelete"
    >
      <div class="rounded-lg bg-red-50 dark:bg-red-800/20 p-4">
        <p class="text-sm text-gray-700 dark:text-gray-300 mb-2">
          <strong>Session:</strong> {{ sessionToDelete?.name || 'Unnamed Session' }}
        </p>
        <div class="text-sm text-red-600 dark:text-red-400 space-y-1">
          <p class="font-medium">This will permanently delete:</p>
          <ul class="list-disc list-inside ml-2 space-y-1">
            <li>Session record and metadata</li>
            <li>All research data and articles</li>
            <li>Vector embeddings and collections</li>
            <li>All associated files</li>
          </ul>
        </div>
      </div>
    </ConfirmationModal>

    <!-- Add Entity Modal -->
    <AddEntityModal
      ref="addEntityModalRef"
      :show="showAddNewEntityForm"
      :entity-type="searchType"
      :company-info="companyInfo"
      :person-info="personInfo"
      :loading="isAddingEntity"
      @confirm="addNewEntity"
      @cancel="showAddNewEntityForm = false"
    />

    <!-- Success Toast Notification -->
    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-2 opacity-0"
    >
      <div
        v-if="showSuccessToast"
        class="fixed bottom-4 right-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg shadow-lg p-4 flex items-center gap-3 z-50"
      >
        <CheckCircleIcon class="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
        <p class="text-sm font-medium text-green-800 dark:text-green-200">
          {{ successToastMessage }}
        </p>
        <button
          @click="showSuccessToast = false"
          class="ml-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useResearchStore } from '@/stores/research'
import { useSessionStore } from '@/stores/session'
import { useProjectStore } from '@/stores/project'
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import BreadCrumbs from '@/layouts/BreadCrumbs.vue'
import PhaseNavigation from '@/components/PhaseNavigation.vue'
import AgentChatSidebar from '@/components/AgentChatSidebar.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import AddEntityModal from '@/components/modals/AddEntityModal.vue'
import { TrashIcon, ChartBarIcon, DocumentPlusIcon, MagnifyingGlassIcon, PlusIcon, PlayIcon, BeakerIcon, DocumentIcon, CheckCircleIcon, CursorArrowRaysIcon, UserIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const route = useRoute()
const sessionStore = useSessionStore()
const projectStore = useProjectStore()
const researchStore = useResearchStore()
const { session } = storeToRefs(sessionStore)
const { loading } = storeToRefs(sessionStore)

const search = ref('')
const searchType = ref('company')
const expandedMoreInfo = ref(null)
const expandedSources = ref(null)
const expandedAdvancedActions = ref(null)
const showAllAlternatives = ref(false)
const hasSearched = ref(false)
const showNewSessionForm = ref(false)
const showAddNewEntityForm = ref(false)
const addEntityModalRef = ref(null)
const isAddingEntity = ref(false)
const showSuccessToast = ref(false)
const successToastMessage = ref('')
const routeSessionId = computed(() => route.query.sessionId)

// Check if collection has started for current session (prevents expensive re-runs)
const hasCollectionStarted = computed(() => {
  const currentSession = sessionStore.session
  return currentSession?._id ? researchStore.hasCollectionStarted(currentSession._id) : false
})

// Agent chat overlay state
const isAgentOpen = ref(false)
const agentPanelRef = ref(null)

// URL validation states


const isSearching = ref(false)

// New entity form data
const newEntityData = ref({
  name: '',
  description: '',
  location: '',
  industry: '',
  website: '',
  jobTitle: '',
  company: '',
  notes: ''
})

// Company info - matching Entity Disambiguation API spec
const companyInfo = ref({
  headquarters: '',  // renamed from location to match API
  industry: '',
  website: '',
  domain: '',        // added per API spec
  ticker: ''         // renamed from stockTicker to match API
})

// Person info - matching Entity Disambiguation API spec
const personInfo = ref({
  location: '',
  company: '',
  jobTitle: '',
  email: '',         // added per API spec
  phone: '',         // added per API spec
  linkedIn: '',
  twitter: ''
})

// Get projectId from URL query
const projectId = computed(() => route.query.projectId)

// Get sessionId from session store or route query
const sessionId = computed(() => session.value?._id || route.query.sessionId)

// Check if we should show the search form automatically (no existing sessions)
const shouldShowSearchForm = computed(() => {
  return !routeSessionId.value && (activeSessions.value.length === 0 || showNewSessionForm.value)
})

// Sessions for this project
const sessions = ref([])

// Load session data function
async function loadSessionData() {
  if (!projectId.value) return
  
  console.log('🔄 Loading session data for project:', projectId.value)
  
  // Use enhanced session fetching with filtering options
  await sessionStore.fetchSessions(projectId.value, {
    // Include all session states for comprehensive view
    sortBy: 'updatedAt',
    sortOrder: 'desc'
  })
  sessions.value = sessionStore.sessions
  
  console.log('📊 Sessions loaded:', {
    count: sessions.value.length,
    phases: sessions.value.reduce((acc, s) => {
      acc[s.phase || 'unknown'] = (acc[s.phase || 'unknown'] || 0) + 1
      return acc
    }, {})
  })
  
  // Also load deleted sessions for restoration options
  await loadDeletedSessions()
  
  const sid = route.query.sessionId
  
  if (sid) {
    await sessionStore.loadSession(projectId.value, sid)
    
    const currentSession = sessionStore.session
    if (currentSession) {
      search.value = currentSession.searchQuery || ''
      searchType.value = currentSession.entityType || 'company'
      hasSearched.value = true
    }
  } else {
    sessionStore.clearSession()
    
    search.value = ''
    searchType.value = 'company'
    hasSearched.value = false
    expandedMoreInfo.value = null
    expandedSources.value = null
  }
}

// Click outside handler for agent panel
const handleClickOutside = (event) => {
  if (agentPanelRef.value && !agentPanelRef.value.contains(event.target) && !event.target.closest('button[class*="fixed bottom-6 right-6"]')) {
    isAgentOpen.value = false
  }
}

// Watch agent open state to add/remove click listener
watch(isAgentOpen, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 100)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

// Load data on mount
onMounted(loadSessionData)

// Watch for route changes and reload session data
watch(
  () => route.query.sessionId,
  (newSessionId, oldSessionId) => {
    if (newSessionId !== oldSessionId) {
      loadSessionData()
    }
  }
)

// Cleanup click listener on unmount
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Format date utility
function formatDate(dateString) {
  if (!dateString) return 'Unknown Date'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Invalid Date'
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Helper function to check if a session is deleted (based on API documentation)
function isSessionDeleted(session) {
  // Primary: sessionState === 'deleted' (main lifecycle state)
  // Secondary: status === 'restorable' (from deleted sessions API)
  // Legacy: deleted === true (backward compatibility)
  return session.sessionState === 'deleted' || session.status === 'restorable' || session.deleted === true
}

function getSessionDisplayDate(session) {
  // Check if this is a deleted session by any indicator
  const isDeleted = session.deleted || session.sessionState === 'deleted' || session.status === 'restorable'
  
  if (isDeleted && session.deletedAt) {
    return session.deletedAt
  }
  if (isDeleted && !session.deletedAt) {
    return session.updatedAt || session.createdAt || new Date().toISOString()
  }
  return session.createdAt
}

// Open a session (view details or resume) - Always goes to Research page for full session overview
function openSession(sessionId) {
  router.push({ name: 'Research', query: { projectId: projectId.value, sessionId } })
}

// Smart session opening - Routes based on session progress and data
function openSessionSmart(sessionId) {
  handleSmartNavigation(sessionId)
}

// Enhanced session deletion state
const deleteSessionId = ref(null)
const deleteType = ref('soft') // Default to soft delete (safe option)
const cascadeData = ref(false)
const deleteReason = ref('cleanup')
const deleteResult = ref(null)
const dataStats = ref(null)
const isSearchAgainFlow = ref(false) // Track if deletion is from "Search Again" button

// Show deleted sessions
const showDeleted = ref(false)
const deletedSessions = ref([])

// Session deletion modal state (for initial soft/hard delete choice)
const showDeleteModal = ref(false)

// Permanent delete modal state  
const showPermanentDeleteModal = ref(false)
const sessionToDelete = ref(null)
const deletingSession = ref(false)

// Enhanced session deletion
async function confirmDeleteSession(sessionId) {
  deleteSessionId.value = sessionId
  deleteType.value = 'soft' // Reset to safe default (soft delete)
  cascadeData.value = false // Reset to safe default
  deleteReason.value = 'cleanup'
  
  // Get data statistics before showing deletion modal (optional)
  // Skip if we know the endpoint is problematic
  try {
    const stats = await sessionStore.getSessionDataStats(projectId.value, sessionId)
    dataStats.value = stats
  } catch (error) {
    console.warn('Could not load data statistics (status:', error.response?.status, ')')
    // Set to null to hide the data stats section entirely
    dataStats.value = null
    // Don't let data stats errors prevent the deletion modal from showing
  }
  
  showDeleteModal.value = true
}

async function deleteSession() {
  if (!deleteSessionId.value) return
  
  try {
    const options = {
      deleteType: deleteType.value,
      cascadeData: cascadeData.value,
      reason: deleteReason.value
    }
    
    console.log('Deleting session:', deleteSessionId.value, 'with options:', options)
    const result = await sessionStore.deleteSession(projectId.value, deleteSessionId.value, options)
    
    // Auto-show deleted sessions if this was a soft delete
    if (deleteType.value === 'soft') {
      setTimeout(() => {
        if (deletedSessionsComputed.value.length > 0 && !showDeleted.value) {
          showDeleted.value = true
        }
      }, 100)
    }
    
    deleteResult.value = result
    
    // Show success message with data impact
    showDeleteSuccessMessage(result)
    
    // Refresh sessions list
    await sessionStore.fetchSessions(projectId.value)
    sessions.value = sessionStore.sessions
    
    // Refresh deleted sessions if showing them
    if (showDeleted.value) {
      await loadDeletedSessions()
    }
    
    // Close modal
    showDeleteModal.value = false
    deleteSessionId.value = null
    
    // If this was from "Search Again" button, show search form for new session
    if (isSearchAgainFlow.value) {
      // Clear current session from store
      sessionStore.clearSession()
      
      // Reset all form state for new session
      showNewSessionForm.value = true
      hasSearched.value = false
      search.value = ''
      searchType.value = 'company'
      expandedMoreInfo.value = null
      expandedSources.value = null
      
      // Navigate to research page without sessionId to show clean form
      await router.push({ 
        name: 'Research', 
        query: { projectId: projectId.value } 
      })
      
      isSearchAgainFlow.value = false // Reset flag
    }
    
  } catch (error) {
    console.error('Failed to delete session:', error)
    
    // Show user-friendly error message
    alert('Session deletion encountered an issue, but the session may have been removed from your view. Please refresh the page to see the current state.')
    
    // Close modal and refresh to show current state
    showDeleteModal.value = false
    deleteSessionId.value = null
    
    // Refresh sessions to show current state
    try {
      await sessionStore.fetchSessions(projectId.value)
      sessions.value = sessionStore.sessions
    } catch (refreshError) {
      console.error('Failed to refresh sessions:', refreshError)
    }
  }
}



async function restoreSession(sessionId) {
  try {
    const result = await sessionStore.restoreSession(projectId.value, sessionId)
    console.log('Session restored successfully:', result.message)
    
    // Refresh both active and deleted sessions lists
    await sessionStore.fetchSessions(projectId.value)
    sessions.value = sessionStore.sessions
    await loadDeletedSessions()
    
    alert('Session restored successfully!')
    
  } catch (error) {
    console.error('Failed to restore session:', error)
    
    // Handle common error cases gracefully
    if (error.response?.status === 404) {
      // Session doesn't exist on server, remove from UI
      console.log('Session not found on server, removing from UI')
      await loadDeletedSessions() // Refresh list to remove it
      alert('Session no longer exists on the server and has been removed from the list.')
    } else if (error.response?.status === 401) {
      alert('Your session has expired. The page will refresh to log you back in.')
      window.location.reload()
    } else {
      alert('Failed to restore session. Please try again or contact support if the problem persists.')
    }
  }
}

// Simple confirmation for permanent deletion of already-deleted sessions
async function confirmPermanentDelete(sessionId, sessionName) {
  // Defensive check - make sure we have valid parameters
  if (!sessionId || !sessionName) {
    console.error('Invalid parameters for permanent delete:', { sessionId, sessionName })
    alert('Error: Invalid session data. Please refresh the page and try again.')
    return
  }
  
  // Store session info and show modal
  sessionToDelete.value = {
    id: sessionId,
    name: sessionName
  }
  showPermanentDeleteModal.value = true
}

async function handlePermanentDelete() {
  if (!sessionToDelete.value) return
  
  const { id: sessionId, name: sessionName } = sessionToDelete.value
  deletingSession.value = true
  
  try {
    const deleteOptions = { 
      deleteType: 'hard',
      cascadeData: true,
      reason: 'User requested permanent deletion of soft-deleted session'
    }
    
    const result = await sessionStore.deleteSession(projectId.value, sessionId, deleteOptions)
    
    // Refresh deleted sessions list to remove it from UI
    await loadDeletedSessions()
    
    // Close modal and reset state
    showPermanentDeleteModal.value = false
    sessionToDelete.value = null
    
  } catch (error) {
    console.error('Failed to permanently delete session:', error)
    
    // Handle common error cases gracefully
    if (error.response?.status === 404) {
      // Session doesn't exist on server - treat as already deleted
      await loadDeletedSessions()
      showPermanentDeleteModal.value = false
      sessionToDelete.value = null
    } else if (error.response?.status === 401) {
      alert('Your session has expired. The page will refresh to log you back in.')
      window.location.reload()
      return
    } else if (error.response?.status >= 500) {
      // Close modal first, then show error
      showPermanentDeleteModal.value = false
      sessionToDelete.value = null
      
      alert('Server error occurred while deleting the session. The server may be temporarily unavailable. Please try again in a moment.')
      await loadDeletedSessions()
    } else {
      // Close modal first, then show error
      showPermanentDeleteModal.value = false
      sessionToDelete.value = null
      
      alert(`Failed to permanently delete session: ${error.response?.data?.message || error.message}`)
      await loadDeletedSessions()
    }
  } finally {
    deletingSession.value = false
  }
}

function cancelPermanentDelete() {
  showPermanentDeleteModal.value = false
  sessionToDelete.value = null
}

async function loadDeletedSessions() {
  try {
    deletedSessions.value = await sessionStore.fetchDeletedSessions(projectId.value)
  } catch (error) {
    console.error('Failed to load deleted sessions:', error)
    deletedSessions.value = []
  }
}

function showDeleteSuccessMessage(result) {
  if (result?.dataStats) {
    const { dataStats, message, deletionType } = result
    console.log(`${message}`)
    console.log(`Data affected: ${dataStats.totalRecords} records (${dataStats.estimatedSizeMB}MB)`)
    if (dataStats.dataCollections) console.log(`- Data Collections: ${dataStats.dataCollections}`)
    if (dataStats.articles) console.log(`- Articles: ${dataStats.articles}`)
    if (dataStats.entityRecords) console.log(`- Entity Records: ${dataStats.entityRecords}`)
    if (dataStats.vectorEmbeddings) console.log(`- Vector Embeddings: ${dataStats.vectorEmbeddings}`)
  }
}

// Add new custom entity via API
async function addNewEntity(formData) {
  isAddingEntity.value = true
  
  try {
    console.log('🆕 Adding manual entity:', formData)
    
    // Ensure we have a session to add the entity to
    let currentSession = session.value
    
    if (!currentSession?._id) {
      console.log('No session exists, creating new session for manual entity')
      const payload = {
        searchQuery: formData.name,
        entityType: searchType.value,
        sessionType: 'identity',
        companyInfo: searchType.value === 'company' ? companyInfo.value : undefined,
        personInfo: searchType.value === 'person' ? personInfo.value : undefined
      }
      
      currentSession = await sessionStore.createSession(projectId.value, payload)
      console.log('Created new session:', currentSession)
    }
    
    const sessionIdForApi = currentSession._id || currentSession.id
    
    // Call API to create manual entity
    const manualEntity = await sessionStore.createManualEntity(
      projectId.value,
      sessionIdForApi,
      {
        name: formData.name,
        entityType: searchType.value,
        description: formData.description,
        additionalInfo: formData.additionalInfo
      }
    )
    
    console.log('✅ Manual entity created:', manualEntity)
    
    // Reload session to get updated data
    await sessionStore.loadSession(projectId.value, sessionIdForApi)
    
    // Auto-select the created entity
    await selectCandidate(manualEntity)
    
    // Close modal and reset form
    showAddNewEntityForm.value = false
    if (addEntityModalRef.value) {
      addEntityModalRef.value.resetForm()
    }
    
    // Show success toast
    successToastMessage.value = 'Entity added successfully!'
    showSuccessToast.value = true
    
    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      showSuccessToast.value = false
    }, 3000)
    
    // Navigate to data collection immediately
    await nextTick()
    goToDataCollection()
    
  } catch (error) {
    console.error('❌ Error adding manual entity:', error)
    
    // Show error in modal (keep modal open)
    const errorMessage = error.response?.data?.error || error.message || 'Failed to add entity. Please try again.'
    
    if (addEntityModalRef.value) {
      addEntityModalRef.value.setError(errorMessage)
    }
  } finally {
    isAddingEntity.value = false
  }
}

function resetNewEntityForm() {
  newEntityData.value = {
    name: '',
    description: '',
    location: '',
    industry: '',
    website: '',
    jobTitle: '',
    company: '',
    notes: ''
  }
}

// Start a new session
async function startNewSession() {
  sessionStore.clearSession()
  
  search.value = ''
  searchType.value = 'company'
  hasSearched.value = false
  expandedMoreInfo.value = null
  expandedSources.value = null
  
  // Reset form fields
  companyInfo.value = {
    ticker: '',
    industry: '',
    phone: '',
    emails: [''],
    websites: [''],
    addresses: [''],
    customFields: [{ label: '', value: '' }]
  }
  
  personInfo.value = {
    fullName: '',
    linkedin: '',
    email: '',
    title: '',
    company: '',
    location: '',
    phone: '',
    twitter: '',
    spouse: '',
    children: '',
    dob: '',
    emails: [''],
    addresses: [''],
    customFields: [{ label: '', value: '' }]
  }
  
  // Reset validation states

  
  await router.push({ name: 'Research', query: { projectId: projectId.value } })
}

// ENHANCED: Updated search logic with structured data support
async function onSearch() {
  if (!search.value || !projectId.value) return
  
  hasSearched.value = true
  isSearching.value = true

  try {

    
    // Create a new session for this search
    const payload = {
      searchQuery: search.value,
      entityType: searchType.value,
      sessionType: 'identity',
      companyInfo: searchType.value === 'company' ? companyInfo.value : undefined,
      personInfo: searchType.value === 'person' ? personInfo.value : undefined
    }
    
    const newSession = await sessionStore.createSession(projectId.value, payload)
    console.log('Created new session with structured data:', newSession)
    
    // ENHANCED: Use the new disambiguateCompany or disambiguatePerson methods
    let response
    const sessionIdForApi = newSession.id || newSession._id
    
    if (searchType.value === 'company') {
      const requestPayload = {
        query: search.value,
        entityType: 'company',
        identifiers: {
          companyName: search.value  // Include company name per API spec
        },
        searchOptions: {
          prioritizeStructuredData: true,
          exactMatchRequired: false,
          includeAliases: true
        }
      }
      
      // Add only non-empty identifiers per API spec
      if (companyInfo.value.headquarters?.trim()) {
        requestPayload.identifiers.headquarters = companyInfo.value.headquarters.trim()
      }
      if (companyInfo.value.industry?.trim()) {
        requestPayload.identifiers.industry = companyInfo.value.industry.trim()
      }
      if (companyInfo.value.website?.trim()) {
        requestPayload.identifiers.website = companyInfo.value.website.trim()
      }
      if (companyInfo.value.domain?.trim()) {
        requestPayload.identifiers.domain = companyInfo.value.domain.trim()
      }
      if (companyInfo.value.ticker?.trim()) {
        requestPayload.identifiers.ticker = companyInfo.value.ticker.trim()
      }
      
      console.log('DEBUG - Company form data:', companyInfo.value)
      console.log('DEBUG - API request payload for company:', JSON.stringify(requestPayload, null, 2))
      
      response = await sessionStore.disambiguateCompany(projectId.value, sessionIdForApi, requestPayload)
    } else {
      const requestPayload = {
        query: search.value,
        entityType: 'person',
        identifiers: {
          fullName: search.value  // Include full name per API spec
        },
        searchOptions: {
          prioritizeStructuredData: true,
          exactMatchRequired: false,
          includeAliases: true
        }
      }
      
      // Add only non-empty identifiers per API spec
      if (personInfo.value.location?.trim()) {
        requestPayload.identifiers.location = personInfo.value.location.trim()
      }
      if (personInfo.value.company?.trim()) {
        requestPayload.identifiers.company = personInfo.value.company.trim()
      }
      if (personInfo.value.jobTitle?.trim()) {
        requestPayload.identifiers.jobTitle = personInfo.value.jobTitle.trim()
      }
      if (personInfo.value.email?.trim()) {
        requestPayload.identifiers.email = personInfo.value.email.trim()
      }
      if (personInfo.value.phone?.trim()) {
        requestPayload.identifiers.phone = personInfo.value.phone.trim()
      }
      if (personInfo.value.linkedIn?.trim()) {
        requestPayload.identifiers.linkedIn = personInfo.value.linkedIn.trim()
      }
      if (personInfo.value.twitter?.trim()) {
        requestPayload.identifiers.twitter = personInfo.value.twitter.trim()
      }
      
      console.log('DEBUG - Person form data:', personInfo.value)
      console.log('DEBUG - API request payload for person:', JSON.stringify(requestPayload, null, 2))
      
      response = await sessionStore.disambiguatePerson(projectId.value, sessionIdForApi, requestPayload)
    }
    
    console.log('Enhanced disambiguation response:', response)
    console.log('Session after disambiguation:', sessionStore.session)
    console.log('Enhanced candidates from store:', sessionStore.candidates)

    // Reload the session to ensure we have the latest data
    const sessionIdForReload = newSession._id || newSession.id
    await sessionStore.loadSession(projectId.value, sessionIdForReload)
    
    console.log('Session after reload:', sessionStore.session)
    console.log('Enhanced disambiguation results:', sessionStore.disambiguationResult)
    console.log('Data quality metrics:', sessionStore.dataQuality)
    console.log('Identifier validation:', sessionStore.identifierValidation)

    // Refresh session list
    await sessionStore.fetchSessions(projectId.value)
    sessions.value = sessionStore.sessions

    // Auto-select first candidate if no selection exists - TEMPORARILY DISABLED FOR DEBUGGING
    // await nextTick()
    // if (sessionStore.entities?.length > 0) {
    //   const selectedEntities = sessionStore.session?.selectedEntities || []
    //   if (selectedEntities.length === 0) {
    //     console.log('Auto-selecting first candidate after search:', sessionStore.entities[0].name)
    //     await selectCandidate(sessionStore.entities[0])
    //   }
    // }
    
    console.log('🔍 After search - Session state:', {
      sessionId: sessionIdForReload,
      entitiesCount: sessionStore.entities?.length || 0,
      selectedEntitiesCount: sessionStore.session?.selectedEntities?.length || 0,
      selectedEntities: sessionStore.session?.selectedEntities
    })

    // Navigate to the session URL with results available
    await router.push({
      name: 'Research',
      query: {
        projectId: projectId.value,
        sessionId: sessionIdForReload
      }
    })
  } catch (error) {
    console.error('Enhanced search failed:', error)
    // Handle error state in UI
  } finally {
    isSearching.value = false
  }
}

// Single entity selection functionality
async function selectCandidate(candidate) {
  if (!candidate || !sessionStore.session?._id) return
  
  console.log('🎯 Selecting candidate:', {
    name: candidate.name,
    aviatoId: candidate.aviatoId,
    id: candidate.id,
    source: candidate.source
  })
  
  // Create single selection array with the selected candidate
  const selection = {
    candidateId: candidate.aviatoId || candidate.id || `${candidate.name}_${candidate.source}`,
    name: candidate.name,
    source: candidate.source,
    entityType: candidate.entityType || 'person',
    confidence: candidate.confidence || candidate.totalScore || 0,
    aviatoId: candidate.aviatoId,
    id: candidate.id,
    context: candidate.context || {},
    metadata: candidate.metadata || {},
    selectedAt: new Date().toISOString()
  }
  
  // Single selection array
  const newSelectedEntities = [selection]
  
  console.log('🎯 New selectedEntities with enhanced data:', newSelectedEntities)
  console.log('🎯 Previous selectedEntities:', sessionStore.session?.selectedEntities)
  
  try {
    const payload = {
      selectedEntities: newSelectedEntities,
      selectedEntity: newSelectedEntities.length === 1 ? newSelectedEntities[0] : null
    }
    
    await sessionStore.updateSession(
      projectId.value, 
      sessionStore.session._id, 
      payload
    )
    
    console.log('Successfully updated entity selection with enhanced data')
    
  } catch (error) {
    console.error('Failed to update candidate selection:', error)
  }
}

function expandMoreInfo(id) {
  expandedMoreInfo.value = expandedMoreInfo.value === id ? null : id
  expandedSources.value = null
}

function expandSources(id) {
  expandedSources.value = expandedSources.value === id ? null : id
}

async function goToDataCollection() {
  if (!sessionStore.session?._id) return
  
  // Session selections are already updated via toggleCandidateSelection
  // Navigate to data collection phase
  router.push({
    name: 'DataCollection',
    query: { projectId: projectId.value, sessionId: sessionStore.session._id }
  })
}

function handleVCardCSV(e) {
  // TODO: Parse vCard/CSV and autofill companyInfo/personInfo fields
}

// Use sessionStore computed properties
const candidates = computed(() => {
  return sessionStore.entities || []
})

const topCandidate = computed(() => {
  // Use the first candidate as the top candidate since API provides ordered results
  return candidates.value?.length ? candidates.value[0] : null
})

// Auto-select first candidate if none is selected - TEMPORARILY DISABLED FOR DEBUGGING
// watch(candidates, async (newCandidates) => {
//   if (newCandidates?.length > 0 && sessionStore.session?._id) {
//     const selectedEntities = sessionStore.session.selectedEntities || []
//     if (selectedEntities.length === 0) {
//       console.log('Auto-selecting first candidate:', newCandidates[0].name)
//       await selectCandidate(newCandidates[0])
//     }
//   }
// }, { immediate: true, flush: 'post' })

// DEBUG: Log current session state
watch(() => sessionStore.session?.selectedEntities, (newSelection) => {
  console.log('🔍 Selection changed:', {
    selectedCount: newSelection?.length || 0,
    selectedEntities: newSelection,
    sessionId: sessionStore.session?._id
  })
}, { deep: true })

const otherCandidates = computed(() => {
  // Show all candidates except the first one (which is the top candidate)
  if (!candidates.value?.length) return []
  return candidates.value.slice(1) // Skip first candidate (top candidate)
})

const selectedCandidates = computed(() => {
  if (!sessionStore.session?.selectedEntities) return []
  
  return sessionStore.session.selectedEntities
    .map(entity => entity.candidateId || `${entity.name}_${entity.source}`)
    .filter(Boolean)
})

const selectedEntities = computed(() => {
  return sessionStore.session?.selectedEntities || []
})

const hasEntitySelected = computed(() => {
  return selectedEntities.value && selectedEntities.value.length > 0
})

const isCandidateSelected = (candidate) => {
  if (!candidate || !sessionStore.session?.selectedEntities?.length) {
    console.log('🔍 isCandidateSelected: No candidate or no selected entities', {
      hasCandidate: !!candidate,
      hasSession: !!sessionStore.session,
      selectedEntitiesLength: sessionStore.session?.selectedEntities?.length || 0
    })
    return false
  }
  
  // Since we only allow single selection, check if this is the selected one
  const selectedEntity = sessionStore.session.selectedEntities[0]
  if (!selectedEntity) {
    console.log('🔍 isCandidateSelected: No selected entity found')
    return false
  }
  
  console.log('🔍 isCandidateSelected comparison:', {
    candidateName: candidate.name,
    candidateAviatoId: candidate.aviatoId,
    candidateId: candidate.id,
    candidateSource: candidate.source,
    selectedEntityName: selectedEntity.name,
    selectedEntityAviatoId: selectedEntity.aviatoId,
    selectedEntityCandidateId: selectedEntity.candidateId,
    selectedEntitySource: selectedEntity.source
  })
  
  // FIXED: Strict matching based on aviatoId only - no fallback to name+source since names can be identical
  const isSelected = (
    // Primary match: aviatoId to aviatoId
    (candidate.aviatoId && selectedEntity.aviatoId && candidate.aviatoId === selectedEntity.aviatoId) ||
    // Secondary match: aviatoId to candidateId field (for backward compatibility)
    (candidate.aviatoId && selectedEntity.candidateId && candidate.aviatoId === selectedEntity.candidateId)
    // REMOVED: name+source fallback because multiple candidates can have identical names
  )
  
  console.log('🔍 isCandidateSelected result:', isSelected)
  return isSelected
}

// Default project data for sidebar when no session is loaded
const defaultProjectData = computed(() => ({
  name: search.value || 'New Research Session',
  _id: null,
  searchQuery: search.value,
  entityType: searchType.value,
  status: 'initializing'
}))

// ENHANCED: Get project information for the sidebar with enhanced metadata
const projectContext = computed(() => {
  const currentProject = projectStore.projects.find(p => p._id === projectId.value)
  const currentSession = session.value
  
  return {
    // Project information
    _id: projectId.value,
    name: currentProject?.name || 'Research Project',
    description: currentProject?.description || 'Entity research and analysis',
    entityType: sessionStore.entityType || searchType.value,
    
    // Current search/session context
    searchQuery: sessionStore.entityName || currentSession?.searchQuery || search.value,
    sessionId: currentSession?._id || null,
    phase: currentSession?.phase || 'disambiguation',
    
    // ENHANCED: Enhanced candidates context with structured data
    candidates: sessionStore.entities || [],
    topCandidate: sessionStore.topEntity || null,
    selectedCandidates: selectedCandidates.value || [],
    
    // ENHANCED: Rich disambiguation metadata with new properties
    hasRichContext: sessionStore.hasRichContext,
    hasVerifiedSources: sessionStore.hasVerifiedSources,
    hasStructuredData: sessionStore.hasStructuredData,
    isHighConfidence: sessionStore.isHighConfidence,
    requiresManualReview: sessionStore.requiresManualReview,
    needsDisambiguation: sessionStore.needsDisambiguation,
    confidence: sessionStore.disambiguationConfidence,
    aviatoId: sessionStore.aviatoId,
    
    // ENHANCED: Data quality and audit trail information
    dataQuality: sessionStore.dataQuality,
    auditTrail: sessionStore.auditTrail,
    identifierValidation: sessionStore.identifierValidation,
    primarySources: sessionStore.primarySources,
    secondarySources: sessionStore.secondarySources,
    
    // Progress indicators
    hasResults: Boolean(sessionStore.entities?.length),
    hasSelections: Boolean(selectedCandidates.value?.length),
    
    // Research status
    status: currentSession?.status || (loading.value ? 'searching' : 'ready'),
    createdAt: currentSession?.createdAt || new Date().toISOString(),
    
    // ENHANCED: Enhanced metadata for AI context
    workflow: {
      currentStep: 'disambiguation',
      nextStep: selectedCandidates.value?.length ? 'questions' : null,
      completedSteps: currentSession?.completedSteps || []
    },
    
    // ENHANCED: Enhanced disambiguation metadata for AI discussion
    disambiguationMetadata: sessionStore.disambiguationMetadata,
    
    // ENHANCED: Structured data context for AI recommendations
    structuredDataContext: {
      inputIdentifiers: currentSession?.structuredIdentifiers || {},
      validationResults: sessionStore.identifierValidation,
      fallbackUsed: sessionStore.auditTrail?.fallbackUsed || false,
      confidenceBreakdown: sessionStore.disambiguationMetadata?.confidenceBreakdown || {}
    }
  }
})

// Session categorization computed properties
const activeSessions = computed(() => {
  const allSessions = sessions.value || []
  const filtered = allSessions.filter(s => !s.deleted && (s.sessionState !== 'archived' && s.status !== 'archived'))
  
  console.log('🔍 activeSessions computed:', {
    totalSessions: allSessions.length,
    filteredSessions: filtered.length,
    phaseDistribution: filtered.reduce((acc, s) => {
      acc[s.phase || 'unknown'] = (acc[s.phase || 'unknown'] || 0) + 1
      return acc
    }, {})
  })
  
  return filtered
})

const deletedSessionsComputed = computed(() => {
  return deletedSessions.value || []
})

const allSessionsDisplay = computed(() => {
  let displaySessions = [...activeSessions.value]
  
  if (showDeleted.value && deletedSessionsComputed.value.length > 0) {
    displaySessions = [...displaySessions, ...deletedSessionsComputed.value]
  }
  
  return displaySessions
})

// Enhanced session metadata from API response
const sessionMetadata = computed(() => {
  return sessionStore.sessionMetadata || null
})

// Enhanced UI/UX Helper Functions
function getPhaseColor(phase) {
  const colors = {
    disambiguation: {
      bg: 'bg-blue-100 dark:bg-blue-900/50',
      text: 'text-blue-600 dark:text-blue-400',
      badge: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      progress: 'bg-blue-600'
    },
    data_collection: {
      bg: 'bg-amber-100 dark:bg-amber-900/50',
      text: 'text-amber-600 dark:text-amber-400',
      badge: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
      progress: 'bg-amber-600'
    },
    analysis: {
      bg: 'bg-purple-100 dark:bg-purple-900/50',
      text: 'text-purple-600 dark:text-purple-400',
      badge: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      progress: 'bg-purple-600'
    },
    reporting: {
      bg: 'bg-green-100 dark:bg-green-900/50',
      text: 'text-green-600 dark:text-green-400',
      badge: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      progress: 'bg-green-600'
    },
    completed: {
      bg: 'bg-emerald-100 dark:bg-emerald-900/50',
      text: 'text-emerald-600 dark:text-emerald-400',
      badge: 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
      progress: 'bg-emerald-600'
    }
  }
  
  return colors[phase] || colors.disambiguation
}

function getPhaseLabel(phase) {
  const labels = {
    disambiguation: 'Entity Disambiguation',
    data_collection: 'Data Collection',
    analysis: 'Analysis & Insights',
    reporting: 'Report Generation',
    completed: 'Completed'
  }
  
  return labels[phase] || 'Active'
}

function getPhaseIcon(phase, isDeleted = false) {
  if (isDeleted) {
    return 'svg' // Return the trash icon as default for deleted items
  }
  
  const icons = {
    disambiguation: 'svg', // Search/magnifying glass
    data_collection: 'svg', // Database/collection
    analysis: 'svg', // Chart/analytics
    reporting: 'svg', // Document/report
    completed: 'svg' // Checkmark/complete
  }
  
  // For now return generic svg, but these could be proper icon components
  return 'svg'
}

// Enhanced Primary Action Handler with Smart Navigation
async function handlePrimaryAction(primaryAction, sessionId) {
  if (!primaryAction || !sessionId) return
  
  try {
    switch (primaryAction.action) {
      case 'disambiguation':
        // Navigate to disambiguation results
        router.push({ 
          name: 'Research', 
          query: { projectId: projectId.value, sessionId } 
        })
        break
        
      case 'data-collection':
        // Navigate to data collection
        router.push({ 
          name: 'DataCollection', 
          query: { projectId: projectId.value, sessionId } 
        })
        break
        
      case 'data-overview':
        // Navigate to data overview (for sessions with existing data)
        router.push({ 
          name: 'DataOverview', 
          query: { projectId: projectId.value, sessionId } 
        })
        break
        
      case 'analysis':
        // Navigate to analysis view
        if (primaryAction.endpoint) {
          router.push(primaryAction.endpoint)
        } else {
          router.push({ 
            name: 'Analyze', 
            query: { projectId: projectId.value, sessionId } 
          })
        }
        break
        
      case 'report':
      case 'final-report':
        // Navigate to report generation/view
        if (primaryAction.endpoint) {
          router.push(primaryAction.endpoint)
        } else {
          router.push({ 
            name: 'Report', 
            query: { projectId: projectId.value, sessionId } 
          })
        }
        break
        
      case 'view-entity':
        // Smart navigation based on session phase
        const session = activeSessions.value.find(s => s._id === sessionId)
        
        if (session) {
          // Use the same smart navigation logic as the button
          handleSmartEntityView(session)
        } else {
          // Fallback if session not found
          router.push({ 
            name: 'Research', 
            query: { projectId: projectId.value, sessionId } 
          })
        }
        break
        
      default:
        // Smart fallback based on session progress
        handleSmartNavigation(sessionId)
    }
  } catch (error) {
    console.error('Failed to handle primary action:', error)
    // Fallback to smart navigation
    handleSmartNavigation(sessionId)
  }
}

// Smart Navigation based on session progress and data
function handleSmartNavigation(sessionId) {
  const session = activeSessions.value.find(s => s._id === sessionId)
  if (!session) {
    openSession(sessionId)
    return
  }
  
  // Determine best navigation based on session state
  const hasData = session.dataCount && session.dataCount.total > 0
  const hasEntity = session.entityInfo && session.entityInfo.primary
  
  if (hasData && hasEntity) {
    // Has collected data - go to data overview
    router.push({ 
      name: 'DataOverview', 
      query: { projectId: projectId.value, sessionId } 
    })
  } else if (hasEntity && session.phase === 'data_collection') {
    // Entity selected but no data yet - go to data collection
    router.push({ 
      name: 'DataCollection', 
      query: { projectId: projectId.value, sessionId } 
    })
  } else if (session.phase === 'disambiguation' || !hasEntity) {
    // Still in disambiguation phase - go to disambiguation results
    router.push({ 
      name: 'Research', 
      query: { projectId: projectId.value, sessionId } 
    })
  } else {
    // Default fallback - go to research page for session overview
    openSession(sessionId)
  }
}

function getTotalDataPoints() {
  return activeSessions.value.reduce((total, session) => {
    return total + (session.dataCount?.total || 0)
  }, 0)
}

// Smart Action Label Generator with Phase-Based Logic
function getSmartActionLabel(session) {
  if (!session) return 'View Details'
  
  const phase = session.phase
  const hasEntity = session.entityInfo && session.entityInfo.primary
  const dataCount = session.dataCount
  
  switch (phase) {
    case 'completed':
      return 'View Final Report'
      
    case 'reporting':
      return 'Generate Report →'
      
    case 'analysis':
      if (dataCount && dataCount.total > 0) {
        const approvedCount = dataCount.approved || 0
        const totalCount = dataCount.total
        const percentage = Math.round((approvedCount / totalCount) * 100)
        return `Review Analysis (${approvedCount}/${totalCount} • ${percentage}%)`
      }
      return 'View Analysis Results →'
      
    case 'data_collection':
      if (dataCount && dataCount.total > 0) {
        const approvedCount = dataCount.approved || 0
        const totalCount = dataCount.total
        const percentage = Math.round((approvedCount / totalCount) * 100)
        return `Review Data (${approvedCount}/${totalCount} • ${percentage}%)`
      }
      return 'Start Data Collection →'
      
    case 'disambiguation':
    default:
      if (hasEntity) {
        return 'Start Data Collection →'
      } else {
        return 'Complete Disambiguation →'
      }
  }
}

// Determine if changing disambiguation would be destructive based on phase
function isDestructiveDisambiguationChange(session) {
  // If session has progressed beyond disambiguation, changing entity would be destructive
  const destructivePhases = ['data_collection', 'analysis', 'reporting', 'completed']
  return session && destructivePhases.includes(session.phase)
}

// Get appropriate warning message for destructive actions
function getDestructiveActionWarning(session) {
  if (!isDestructiveDisambiguationChange(session)) return null
  
  const phase = session.phase
  const dataCount = session.dataCount?.total || 0
  
  switch (phase) {
    case 'data_collection':
      return dataCount > 0 
        ? `This session has ${dataCount} data points collected. Changing the entity will require starting data collection over.`
        : 'This session is in data collection phase. Changing the entity will reset progress.'
    case 'analysis':
      return 'This session has completed data collection and is in analysis phase. Changing the entity will lose all progress.'
    case 'reporting':
      return 'This session has completed analysis and is in reporting phase. Changing the entity will lose all progress.'
    case 'completed':
      return 'This session is completed. Changing the entity will lose all progress and require starting over.'
    default:
      return 'Changing the entity will reset session progress.'
  }
}

// Smart Action Icon Component Generator
function getActionIconComponent(session) {
  // First check if session has a primaryAction with an icon component name
  if (session.primaryAction?.icon) {
    const iconMap = {
      'DocumentIcon': DocumentIcon,
      'ChartBarIcon': ChartBarIcon, 
      'BeakerIcon': BeakerIcon,
      'PlayIcon': PlayIcon,
      'CursorArrowRaysIcon': CursorArrowRaysIcon,
      'UserIcon': UserIcon
    }
    return iconMap[session.primaryAction.icon] || PlayIcon
  }
  
  // Fallback to smart icon based on session state
  if (!session) return UserIcon
  
  const hasData = session.dataCount && session.dataCount.total > 0
  const hasEntity = session.entityInfo && session.entityInfo.primary
  const isCompleted = session.phase === 'completed'
  
  if (isCompleted) {
    return DocumentIcon
  } else if (hasData && hasEntity) {
    return ChartBarIcon  // Data overview icon
  } else if (hasEntity && session.phase === 'data_collection') {
    return PlayIcon  // Start collection icon
  } else if (session.phase === 'disambiguation' || !hasEntity) {
    return CursorArrowRaysIcon  // Disambiguation icon
  } else if (session.phase === 'analysis') {
    return BeakerIcon  // Analysis icon
  } else if (session.phase === 'reporting') {
    return DocumentIcon  // Report icon
  } else {
    return PlayIcon  // Default action icon
  }
}

// Legacy function - keeping for compatibility but now returns component names
function getActionIcon(session) {
  // This function now returns component names instead of emojis for legacy compatibility
  const component = getActionIconComponent(session)
  
  if (component === DocumentIcon) return 'DocumentIcon'
  if (component === ChartBarIcon) return 'ChartBarIcon'
  if (component === BeakerIcon) return 'BeakerIcon'
  if (component === PlayIcon) return 'PlayIcon'
  if (component === CursorArrowRaysIcon) return 'CursorArrowRaysIcon'
  if (component === UserIcon) return 'UserIcon'
  
  return 'PlayIcon'
}

// Advanced Actions Management
function toggleAdvancedActions(sessionId) {
  expandedAdvancedActions.value = expandedAdvancedActions.value === sessionId ? null : sessionId
}

// Navigation helpers for advanced actions
function navigateToDataOverview(sessionId) {
  router.push({ 
    name: 'DataOverview', 
    query: { projectId: projectId.value, sessionId } 
  })
}

function navigateToDataCollection(sessionId) {
  router.push({ 
    name: 'DataCollection', 
    query: { projectId: projectId.value, sessionId } 
  })
}

// Smart entity view handling
function handleSmartEntityView(session) {
  const phase = session.phase
  const hasEntity = session.entityInfo?.primary
  
  console.log('🚀 handleSmartEntityView called with session:', {
    sessionId: session._id || session.id,
    searchQuery: session.searchQuery,
    phase,
    hasEntity,
    entityInfo: session.entityInfo,
    status: session.status,
    sessionState: session.sessionState
  })
  
  switch (phase) {
    case 'data_collection':
      console.log('📊 Routing to DataCollection (data collection phase)')
      router.push({ 
        name: 'DataCollection', 
        query: { 
          projectId: projectId.value, 
          sessionId: session._id || session.id
        } 
      })
      break
      
    case 'analysis':
    case 'reporting':
    case 'completed':
      console.log('� Routing to DataOverview (analysis/reporting/completed phase)')
      router.push({ 
        name: 'DataOverview', 
        query: { 
          projectId: projectId.value, 
          sessionId: session._id || session.id,
          tab: 'entity'
        } 
      })
      break
      
    case 'disambiguation':
    default:
      if (hasEntity && phase === 'disambiguation') {
        console.log('🚀 Entity selected but still in disambiguation - routing to DataCollection')
        router.push({ 
          name: 'DataCollection', 
          query: { 
            projectId: projectId.value, 
            sessionId: session._id || session.id
          } 
        })
      } else {
        console.log('🔍 Routing to Research (disambiguation needed)')
        router.push({ 
          name: 'Research', 
          query: { 
            projectId: projectId.value, 
            sessionId: session._id || session.id 
          } 
        })
      }
      break
  }
}

// Confirm viewing disambiguation when data exists (potentially confusing)
function confirmViewDisambiguation(session) {
  const dataCount = session.dataCount.total
  const confirmed = confirm(
    `⚠️ Note: This session has ${dataCount} data points collected.\n\n` +
    `Going back to disambiguation results might be confusing since you've already selected an entity and collected data.\n\n` +
    `Consider using "Data Overview" instead to see entity information in the context of your collected data.\n\n` +
    `Are you sure you want to view disambiguation results?`
  )
  
  if (confirmed) {
    router.push({ 
      name: 'Research', 
      query: { projectId: projectId.value, sessionId: session._id } 
    })
  }
}

// Handle search again - trigger delete modal
function handleSearchAgain() {
  const currentSession = sessionStore.session
  
  if (!currentSession?._id) {
    // No session exists, just reset to search form
    showNewSessionForm.value = true
    hasSearched.value = false
    return
  }
  
  // Set flag to indicate this deletion is for starting a new search
  isSearchAgainFlow.value = true
  
  // Trigger delete modal - user will see all options and warnings
  confirmDeleteSession(currentSession._id)
}

// Confirm changing entity (potentially destructive)
function confirmChangeEntity(session) {
  if (isDestructiveDisambiguationChange(session)) {
    const dataCount = session.dataCount.total
    const confirmed = confirm(
      `⚠️ WARNING: This will reset all progress\n\n` +
      `This session has ${dataCount} data points collected. ` +
      `Changing the entity will delete all collected data and start over.\n\n` +
      `Are you sure you want to continue?`
    )
    
    if (!confirmed) return
  }
  
  // Navigate to disambiguation with reset flag
  router.push({ 
    name: 'Research', 
    query: { 
      projectId: projectId.value, 
      sessionId: session._id,
      reset: 'entity'
    } 
  })
}

// Smart Entity Button UX Functions
function getEntityButtonText(session) {
  const phase = session.phase
  const hasEntity = session.entityInfo?.primary
  
  console.log('🔍 getEntityButtonText called:', {
    sessionId: session._id || session.id,
    phase,
    hasEntity,
    entityInfo: session.entityInfo,
    calledAt: new Date().toISOString()
  })
  
  switch (phase) {
    case 'data_collection':
    case 'analysis':
    case 'reporting':
    case 'completed':
      console.log('📊 Returning: View Entity & Data')
      return 'View Entity & Data'
    case 'disambiguation':
      if (hasEntity) {
        console.log('✅ Returning: View Entity Details')
        return 'View Entity Details'
      } else {
        console.log('🔍 Returning: View Search Results')
        return 'View Search Results'
      }
    default:
      console.log('❓ Returning: View Session Details (default)')
      return 'View Session Details'
  }
}

function getEntityButtonDestination(session) {
  const phase = session.phase
  const hasEntity = session.entityInfo?.primary
  
  switch (phase) {
    case 'data_collection':
      return 'Data Collection'
    case 'analysis':
    case 'reporting':
    case 'completed':
      return 'Data Overview'
    case 'disambiguation':
      if (hasEntity) {
        return 'Data Collection'
      } else {
        return 'Disambiguation'
      }
    default:
      return 'Session Details'
  }
}

function getEntityButtonStyle(session) {
  const phase = session.phase
  const hasEntity = session.entityInfo?.primary
  
  console.log('🎨 getEntityButtonStyle debug:', {
    sessionId: session._id || session.id,
    phase,
    hasEntity,
    resultingStyle: getStyleForPhase(phase, hasEntity)
  })
  
  switch (phase) {
    case 'analysis':
    case 'reporting':
    case 'completed':
      // Blue for sessions with data
      return 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800'
    case 'data_collection':
      // Green for ready to collect
      return 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800'
    case 'disambiguation':
      if (hasEntity) {
        // Green for entity selected, ready for data collection
        return 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800'
      } else {
        // Gray for needs disambiguation
        return 'text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
      }
    default:
      return 'text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
  }
}

// Helper function for debugging
function getStyleForPhase(phase, hasEntity) {
  switch (phase) {
    case 'analysis':
    case 'reporting':
    case 'completed':
      return 'blue (has data)'
    case 'data_collection':
      return 'green (ready to collect)'
    case 'disambiguation':
      return hasEntity ? 'green (entity selected)' : 'gray (needs disambiguation)'
    default:
      return 'gray (default)'
  }
}

function getEntityButtonIcon(session) {
  const hasData = session.dataCount?.total > 0
  const hasEntity = session.entityInfo?.primary
  
  if (hasData && hasEntity) {
    return ChartBarIcon
  } else if (hasEntity && !hasData) {
    return DocumentPlusIcon
  } else {
    return MagnifyingGlassIcon
  }
}

// Click outside handler for advanced actions
function handleClickOutsideAdvancedActions(event) {
  // Close advanced actions menu if clicking outside
  if (expandedAdvancedActions.value && !event.target.closest('[data-advanced-actions]')) {
    expandedAdvancedActions.value = null
  }
}

// Load project data on mount if not already loaded
onMounted(async () => {
  if (!projectStore.projects.length) {
    await projectStore.fetchProjects()
  }
  
  await loadSessionData()
  
  // Add click outside listener
  document.addEventListener('click', handleClickOutsideAdvancedActions)
})

// URL validation and formatting utilities
function formatWebsiteURL(url) {
  if (!url || typeof url !== 'string') return url
  
  const trimmedUrl = url.trim()
  if (!trimmedUrl) return trimmedUrl
  
  // If already has a protocol, return as is
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl
  }
  
  // Add https:// prefix
  return `https://${trimmedUrl}`
}

function validateWebsiteURL(url) {
  if (!url || !url.trim()) return { isValid: true, message: '' }
  
  const formattedUrl = formatWebsiteURL(url)
  
  try {
    const urlObj = new URL(formattedUrl)
    
    // Basic validation
    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return { isValid: false, message: 'Please enter a valid domain name' }
    }
    
    // Check for valid TLD (at least one dot)
    if (!urlObj.hostname.includes('.')) {
      return { isValid: false, message: 'Please enter a valid domain with a TLD (e.g., .com)' }
    }
    
    return { isValid: true, message: '', formattedUrl }
  } catch (error) {
    return { isValid: false, message: 'Please enter a valid website URL' }
  }
}

// Website URL handlers
function handleWebsiteBlur(index) {
  const website = companyInfo.value.websites[index]
  if (website && website.trim()) {
    const validation = validateWebsiteURL(website)
    websiteValidation.value[index] = validation
    
    if (validation.isValid && validation.formattedUrl) {
      // Show a brief visual indicator that URL was formatted
      const originalUrl = website
      companyInfo.value.websites[index] = validation.formattedUrl
      
      // If URL was formatted, show a subtle indicator
      if (originalUrl !== validation.formattedUrl) {
        // You could add a temporary success indicator here
        setTimeout(() => {
          websiteValidation.value[index] = { ...validation, wasFormatted: false }
        }, 2000)
        websiteValidation.value[index] = { ...validation, wasFormatted: true }
      }
    }
  } else {
    // Clear validation for empty fields
    websiteValidation.value[index] = { isValid: true, message: '' }
  }
}

function removeWebsite(index) {
  companyInfo.value.websites.splice(index, 1)
  // Clean up validation state for removed website
  const newValidation = {}
  Object.keys(websiteValidation.value).forEach(key => {
    const idx = parseInt(key)
    if (idx < index) {
      newValidation[idx] = websiteValidation.value[idx]
    } else if (idx > index) {
      newValidation[idx - 1] = websiteValidation.value[idx]
    }
    // Skip the removed index
  })
  websiteValidation.value = newValidation
}

onUnmounted(() => {
  // Remove click outside listener for advanced actions
  document.removeEventListener('click', handleClickOutsideAdvancedActions)
  // Agent panel click listener is removed via watch cleanup
})
</script>